import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import Editor from '@monaco-editor/react';
import { toast } from 'sonner';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { cn } from '../../utils/cn';
import api, { getApiBaseUrl } from '../../utils/api';
import useProctor from './hooks/useProctor';
import ProctorPanel from './components/ProctorPanel';
import ProctorConsentBanner from './components/ProctorConsentBanner';
import '../landing-page/landing.css';
import './proctor.css';

const STORAGE_KEY = 'prepxai_business_interview_rooms';

const questionBank = [
  {
    id: 'two-sum-business',
    title: 'Pair Sum With Constraints',
    difficulty: 'Easy',
    duration: '25 min',
    topics: ['Hash map', 'Arrays', 'Complexity'],
    prompt:
      'Given an array of integers nums and an integer target, return indices of the two numbers such that they add up to target. Explain the time and space tradeoffs before coding.',
    examples: [
      { input: 'nums = [2, 7, 11, 15], target = 9', output: '[0, 1]' },
      { input: 'nums = [3, 2, 4], target = 6', output: '[1, 2]' },
    ],
    constraints: ['Exactly one valid answer exists.', 'The same element cannot be used twice.', 'Optimize for repeated lookups.'],
  },
  {
    id: 'rate-limiter',
    title: 'Design a Sliding Window Rate Limiter',
    difficulty: 'Medium',
    duration: '40 min',
    topics: ['System design', 'Queues', 'Concurrency'],
    prompt:
      'Implement a rate limiter that allows at most N requests per user in a rolling time window. Discuss cleanup, storage shape, and concurrent request behavior.',
    examples: [
      { input: 'limit = 3, window = 60s, requests at 1s, 5s, 9s, 61s', output: 'allow, allow, allow, allow' },
      { input: 'limit = 2, window = 10s, requests at 1s, 2s, 3s', output: 'allow, allow, reject' },
    ],
    constraints: ['Per-user limits.', 'Rolling window, not fixed buckets.', 'Keep memory bounded.'],
  },
  {
    id: 'nested-comments',
    title: 'Render Nested Comments',
    type: 'coding',
    difficulty: 'Medium',
    duration: '35 min',
    topics: ['Trees', 'Frontend', 'Recursion'],
    prompt:
      'Given a flat list of comments with id and parentId, build the nested tree and render the order from newest top-level comments to oldest replies.',
    examples: [
      { input: '[{id: 1}, {id: 2, parentId: 1}, {id: 3}]', output: 'comment 3, comment 1 -> comment 2' },
    ],
    constraints: ['Handle missing parents gracefully.', 'Avoid O(n^2) scans.', 'Support deeply nested replies.'],
  },
  {
    id: 'candidate-profile-card',
    title: 'Build a Candidate Profile Card',
    type: 'frontend',
    preferredLanguage: 'react',
    difficulty: 'Easy',
    duration: '30 min',
    topics: ['React', 'HTML', 'CSS', 'Accessibility'],
    prompt:
      'Build a responsive candidate profile card. Show the candidate name, role, skill tags, interview status, and a clear action button. The interviewer should be able to see the UI update in the live preview.',
    examples: [
      { input: 'Viewport: 390px mobile', output: 'Card stacks cleanly and button remains visible' },
      { input: 'Viewport: desktop', output: 'Card uses horizontal spacing without text overlap' },
    ],
    constraints: ['Use semantic HTML.', 'Keep the layout responsive.', 'Do not rely on external assets.', 'Make focus/hover states visible.'],
  },
  {
    id: 'top-customers-sql',
    title: 'Find Top Customers by Spend',
    type: 'sql',
    preferredLanguage: 'sql',
    difficulty: 'Medium',
    duration: '25 min',
    topics: ['SQL', 'JOIN', 'GROUP BY', 'Aggregation'],
    prompt:
      'Write a SQL query that returns the top 3 customers by total order amount. Include each customer name and total spend, sorted from highest to lowest.',
    examples: [
      { input: 'users + orders sample tables', output: 'Ava Patel | 2450, Noah Kim | 1780, Maya Singh | 1220' },
    ],
    constraints: ['Join users to orders.', 'Use SUM for total spend.', 'Group by customer.', 'Sort by total spend descending.', 'Limit the output to 3 rows.'],
    schema: [
      { table: 'users', columns: ['id INT', 'name TEXT', 'plan TEXT'] },
      { table: 'orders', columns: ['id INT', 'user_id INT', 'amount NUMERIC', 'created_at DATE'] },
    ],
    sampleRows: [
      { name: 'Ava Patel', total_spend: 2450 },
      { name: 'Noah Kim', total_spend: 1780 },
      { name: 'Maya Singh', total_spend: 1220 },
    ],
  },
];

const languages = [
  { value: 'javascript', label: 'JavaScript' },
  { value: 'python', label: 'Python' },
  { value: 'java', label: 'Java' },
  { value: 'cpp', label: 'C++' },
  { value: 'react', label: 'React' },
  { value: 'html', label: 'HTML/CSS' },
  { value: 'sql', label: 'SQL' },
];

const starterCode = {
  javascript: `function solve(input) {
  // Talk through the approach, then code here.
  return input;
}
`,
  python: `def solve(data):
    # Talk through the approach, then code here.
    return data
`,
  java: `class Solution {
    public Object solve(Object input) {
        // Talk through the approach, then code here.
        return input;
    }
}
`,
  cpp: `#include <bits/stdc++.h>
using namespace std;

int main() {
    // Talk through the approach, then code here.
    return 0;
}
`,
  react: `const skills = ['React', 'Accessibility', 'CSS'];

function CandidateCard() {
  return (
    <main className="page-shell">
      <article className="candidate-card">
        <div>
          <p className="eyebrow">Frontend interview</p>
          <h1>Ava Patel</h1>
          <p className="role">Senior Frontend Engineer</p>
        </div>

        <div className="skills">
          {skills.map((skill) => <span key={skill}>{skill}</span>)}
        </div>

        <div className="status-row">
          <span className="status">Ready for live round</span>
          <button type="button">Review profile</button>
        </div>
      </article>
    </main>
  );
}

ReactDOM.createRoot(document.getElementById('root')).render(<CandidateCard />);
`,
  html: `<!doctype html>
<html>
  <head>
    <style>
      body {
        margin: 0;
        min-height: 100vh;
        display: grid;
        place-items: center;
        font-family: Inter, system-ui, sans-serif;
        background: #f4f7f6;
        color: #10201b;
      }
      .card {
        width: min(92vw, 520px);
        border: 1px solid #dce5e1;
        border-radius: 16px;
        background: white;
        padding: 28px;
        box-shadow: 0 18px 50px rgba(16, 32, 27, 0.12);
      }
      button {
        border: 0;
        border-radius: 999px;
        background: #169873;
        color: white;
        padding: 12px 18px;
      }
    </style>
  </head>
  <body>
    <article class="card">
      <p>Frontend interview</p>
      <h1>Ava Patel</h1>
      <p>Senior Frontend Engineer</p>
      <button>Review profile</button>
    </article>
  </body>
</html>
`,
  sql: `SELECT
  u.name,
  SUM(o.amount) AS total_spend
FROM users u
JOIN orders o ON o.user_id = u.id
GROUP BY u.id, u.name
ORDER BY total_spend DESC
LIMIT 3;
`,
};

const participantPalette = ['#169873', '#0ea5e9', '#f59e0b', '#8b5cf6'];

const isFrontendLanguage = (language) => language === 'react' || language === 'html';
const isSqlLanguage = (language) => language === 'sql';

const getEditorLanguage = (language) => {
  if (language === 'cpp') return 'cpp';
  if (language === 'react') return 'javascript';
  if (language === 'sql') return 'sql';
  if (language === 'html') return 'html';
  return language;
};

const escapeScriptContent = (value = '') => value.replace(/<\/script/gi, '<\\/script');

const buildFrontendPreviewSrcDoc = (code, language) => {
  if (language === 'html') return code;

  return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <style>
      :root { color-scheme: light; }
      body {
        margin: 0;
        min-height: 100vh;
        font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
        background: #f5f8f7;
        color: #0f1f1a;
      }
      .page-shell {
        min-height: 100vh;
        display: grid;
        place-items: center;
        padding: 32px;
      }
      .candidate-card {
        width: min(92vw, 520px);
        border: 1px solid #dce5e1;
        border-radius: 18px;
        background: #ffffff;
        padding: 28px;
        box-shadow: 0 18px 50px rgba(15, 31, 26, 0.12);
      }
      .eyebrow {
        margin: 0 0 8px;
        font-size: 11px;
        letter-spacing: 0.18em;
        text-transform: uppercase;
        color: #169873;
      }
      h1 {
        margin: 0;
        font-size: clamp(28px, 6vw, 42px);
        line-height: 1.05;
      }
      .role {
        margin: 10px 0 0;
        color: #62716c;
      }
      .skills {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        margin-top: 22px;
      }
      .skills span {
        border: 1px solid #dce5e1;
        border-radius: 999px;
        padding: 7px 10px;
        font-size: 13px;
        color: #43524d;
      }
      .status-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 14px;
        margin-top: 24px;
      }
      .status {
        color: #169873;
        font-size: 14px;
      }
      button {
        border: 0;
        border-radius: 999px;
        background: #169873;
        color: white;
        padding: 12px 18px;
        font-weight: 600;
        cursor: pointer;
      }
      button:focus-visible {
        outline: 3px solid rgba(22, 152, 115, 0.25);
        outline-offset: 3px;
      }
      @media (max-width: 520px) {
        .status-row {
          align-items: stretch;
          flex-direction: column;
        }
      }
    </style>
  </head>
  <body>
    <div id="root"></div>
    <script crossorigin src="https://unpkg.com/react@18/umd/react.development.js"></script>
    <script crossorigin src="https://unpkg.com/react-dom@18/umd/react-dom.development.js"></script>
    <script src="https://unpkg.com/@babel/standalone/babel.min.js"></script>
    <script type="text/babel">${escapeScriptContent(code)}</script>
  </body>
</html>`;
};

const runSqlValidation = (query, question) => {
  const normalized = query.toLowerCase();
  const checks = [
    { label: 'Selects customer name', passed: /\bselect\b[\s\S]*\bname\b/.test(normalized) },
    { label: 'Uses SUM for total spend', passed: /sum\s*\(/.test(normalized) },
    { label: 'Joins users and orders', passed: /\bjoin\b[\s\S]*\borders\b/.test(normalized) && /\busers\b/.test(normalized) },
    { label: 'Groups by customer', passed: /\bgroup\s+by\b/.test(normalized) },
    { label: 'Sorts highest spend first', passed: /\border\s+by\b[\s\S]*(desc|total_spend)/.test(normalized) },
    { label: 'Limits to top 3', passed: /\blimit\s+3\b/.test(normalized) },
  ];
  const passed = checks.filter((check) => check.passed).length;

  return {
    mode: 'sql',
    status: passed === checks.length ? 'Validated' : 'Needs review',
    checks,
    passed,
    total: checks.length,
    columns: ['name', 'total_spend'],
    rows: question?.sampleRows || [],
  };
};

const getParticipantId = (roomId) => {
  const key = `prepxai_business_interview_participant_${roomId}`;
  const existing = sessionStorage.getItem(key);
  if (existing) return existing;
  const id = window.crypto?.randomUUID?.() || `participant-${Math.random().toString(36).slice(2, 11)}`;
  sessionStorage.setItem(key, id);
  return id;
};

const buildBusinessRoomWsUrl = (roomId, profile) => {
  const explicitUrl = import.meta.env.VITE_BUSINESS_INTERVIEW_WS_URL;
  let baseUrl = explicitUrl || getApiBaseUrl();

  // If baseUrl contains localhost but we are accessing via local network IP or custom hostname,
  // dynamically replace localhost with the window's hostname.
  if (!explicitUrl && baseUrl.includes('localhost') && window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
    baseUrl = baseUrl.replace('localhost', window.location.hostname);
  }

  const url = new URL(explicitUrl ? `${baseUrl.replace(/\/$/, '')}/${roomId}` : `/api/business/interviews/${roomId}/ws`, baseUrl);
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  url.searchParams.set('participantId', profile.participantId);
  url.searchParams.set('name', profile.name);
  url.searchParams.set('role', profile.role);
  return url.toString();
};

const useLocalMedia = ({ micOn, cameraOn }) => {
  const [stream, setStream] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    const startMedia = async () => {
      if (!navigator.mediaDevices?.getUserMedia) {
        setError('Camera and microphone are not available in this browser (requires secure HTTPS context).');
        return;
      }

      try {
        let mediaStream;
        try {
          mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
        } catch {
          try {
            mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
          } catch {
            mediaStream = await navigator.mediaDevices.getUserMedia({ audio: false, video: true });
          }
        }

        if (cancelled) {
          mediaStream.getTracks().forEach((track) => track.stop());
          return;
        }
        setStream(mediaStream);
        setError('');
      } catch (mediaError) {
        setError(mediaError?.message || 'Camera or microphone access was blocked or is unavailable.');
      }
    };

    startMedia();

    return () => {
      cancelled = true;
      setStream((current) => {
        current?.getTracks().forEach((track) => track.stop());
        return null;
      });
    };
  }, []);

  useEffect(() => {
    stream?.getAudioTracks().forEach((track) => {
      track.enabled = micOn;
    });
  }, [micOn, stream]);

  useEffect(() => {
    stream?.getVideoTracks().forEach((track) => {
      track.enabled = cameraOn;
    });
  }, [cameraOn, stream]);

  return { stream, error };
};

const useAudioLevel = (stream, enabled) => {
  const [level, setLevel] = useState(0);

  useEffect(() => {
    if (!stream || !enabled) {
      setLevel(0);
      return undefined;
    }

    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass || !stream.getAudioTracks().length) {
      setLevel(0);
      return undefined;
    }

    let rafId;
    let audioContext;
    let analyser;
    let source;

    try {
      audioContext = new AudioContextClass();
      analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);
      const bins = new Uint8Array(analyser.frequencyBinCount);

      const tick = () => {
        analyser.getByteFrequencyData(bins);
        const average = bins.reduce((sum, value) => sum + value, 0) / bins.length;
        setLevel(Math.min(100, Math.round((average / 140) * 100)));
        rafId = requestAnimationFrame(tick);
      };

      tick();
    } catch {
      setLevel(0);
    }

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      source?.disconnect();
      analyser?.disconnect();
      audioContext?.close?.();
    };
  }, [enabled, stream]);

  return level;
};

const useBusinessRoomSync = ({ roomId, profile, onMessage }) => {
  const [status, setStatus] = useState('connecting');
  const wsRef = useRef(null);
  const channelRef = useRef(null);
  const onMessageRef = useRef(onMessage);

  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  useEffect(() => {
    if (!roomId || !profile?.participantId) return undefined;

    const channelName = `prepxai-business-room-${roomId}`;
    const channel = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(channelName) : null;
    channelRef.current = channel;

    if (channel) {
      channel.onmessage = (event) => {
        const message = event.data;
        if (!message || message.sender_id === profile.participantId) return;
        onMessageRef.current?.(message, 'local');
      };
    }

    const storageHandler = (event) => {
      if (event.key !== channelName || !event.newValue) return;
      try {
        const message = JSON.parse(event.newValue);
        if (!message || message.sender_id === profile.participantId) return;
        onMessageRef.current?.(message, 'local');
      } catch {
        // Ignore malformed local sync events.
      }
    };

    window.addEventListener('storage', storageHandler);

    let websocket;
    try {
      websocket = new WebSocket(buildBusinessRoomWsUrl(roomId, profile));
      wsRef.current = websocket;
      websocket.onopen = () => setStatus('realtime');
      websocket.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          onMessageRef.current?.(message, 'websocket');
        } catch {
          // Ignore malformed websocket events.
        }
      };
      websocket.onerror = () => {
        if (websocket.readyState !== WebSocket.OPEN) setStatus('local');
      };
      websocket.onclose = () => {
        setStatus((current) => (current === 'realtime' ? 'local' : current));
      };
    } catch {
      setStatus('local');
    }

    const fallbackTimer = window.setTimeout(() => {
      if (!websocket || websocket.readyState !== WebSocket.OPEN) {
        setStatus('local');
      }
    }, 1600);

    return () => {
      window.clearTimeout(fallbackTimer);
      window.removeEventListener('storage', storageHandler);
      channel?.close();
      if (channelRef.current === channel) {
        channelRef.current = null;
      }
      if (websocket && websocket.readyState <= WebSocket.OPEN) {
        websocket.close();
      }
      if (wsRef.current === websocket) {
        wsRef.current = null;
      }
    };
  }, [roomId, profile]);

  const send = useCallback((type, payload = {}) => {
    if (!roomId || !profile?.participantId) return;

    const message = {
      type,
      room_id: roomId,
      sender_id: profile.participantId,
      payload,
      sent_at: new Date().toISOString(),
    };

    try {
      channelRef.current?.postMessage(message);
    } catch {
      channelRef.current = null;
    }

    try {
      localStorage.setItem(`prepxai-business-room-${roomId}`, JSON.stringify({ ...message, nonce: Math.random() }));
    } catch {
      // localStorage can be disabled in private browsing.
    }

    const websocket = wsRef.current;
    if (websocket?.readyState === WebSocket.OPEN) {
      try {
        websocket.send(JSON.stringify({ type, payload }));
      } catch {
        wsRef.current = null;
      }
    }
  }, [profile?.participantId, roomId]);

  return { send, status };
};

const readRooms = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
  } catch {
    return {};
  }
};

const writeRooms = (rooms) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(rooms));
};

const createRoomId = () => {
  const randomPart = window.crypto?.randomUUID?.().slice(0, 8) || Math.random().toString(36).slice(2, 10);
  return `px-${randomPart}`;
};

const getInitials = (name) => {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('') || 'PX';
};

const defaultRoom = (roomId) => ({
  id: roomId,
  title: 'Senior frontend coding interview',
  role: 'Frontend Engineer',
  team: 'Product Engineering',
  provider: 'LiveKit or Daily ready',
  selectedQuestionId: '',
  aiEnabled: false,
  createdAt: new Date().toISOString(),
});

const readRoom = (roomId) => {
  if (!roomId) return null;
  const rooms = readRooms();
  return rooms[roomId] || defaultRoom(roomId);
};

const saveRoom = (room) => {
  const rooms = readRooms();
  rooms[room.id] = room;
  writeRooms(rooms);
};

const ShellHeader = ({ compact = false }) => (
  <header className="border-b border-[var(--lp-line)] bg-white/90 backdrop-blur-md">
    <div className={cn('mx-auto flex h-16 items-center justify-between px-5 sm:px-6 lg:px-8', compact ? 'max-w-[1600px]' : 'max-w-7xl')}>
      <Link to="/for-businesses" className="text-xl tracking-tight text-[var(--lp-ink)]">
        prepx<span className="text-[var(--lp-accent)]">ai</span>
        <span className="ml-2 hidden text-sm text-[var(--lp-ink-soft)] sm:inline">live interview</span>
      </Link>
      <div className="flex items-center gap-2">
        <span className="hidden rounded-full border border-[var(--lp-line)] px-3 py-1.5 font-mono text-[10px] tracking-[0.18em] text-[var(--lp-ink-soft)] sm:inline-flex">
          PHASE 1
        </span>
        <Button
          asChild
          variant="outline"
          size="sm"
          className="h-9 rounded-full border-[var(--lp-line)] bg-white px-4 text-xs text-[var(--lp-ink)] hover:bg-[var(--lp-surface)]"
        >
          <Link to="/for-businesses">Business page</Link>
        </Button>
      </div>
    </div>
  </header>
);

const CreateInterviewScreen = () => {
  const navigate = useNavigate();
  const [title, setTitle] = useState('Senior frontend coding interview');
  const [role, setRole] = useState('Frontend Engineer');
  const [team, setTeam] = useState('Product Engineering');
  const [aiEnabled, setAiEnabled] = useState(false);

  const handleCreate = () => {
    const id = createRoomId();
    saveRoom({
      id,
      title,
      role,
      team,
      provider: 'LiveKit or Daily ready',
      selectedQuestionId: '',
      aiEnabled,
      createdAt: new Date().toISOString(),
    });
    navigate(`/for-businesses/interviews/${id}?created=1`);
  };

  return (
    <div className="landing-page min-h-screen bg-white text-[var(--lp-ink)]">
      <main className="mx-auto grid min-h-screen max-w-7xl items-center gap-12 px-5 py-12 sm:px-6 lg:grid-cols-[0.85fr_1.15fr] lg:px-8">
        <section>
          <p className="font-mono text-[11px] tracking-[0.25em] text-[var(--lp-accent)]">CREATE INTERVIEW</p>
          <h1 className="mt-4 text-4xl leading-tight tracking-tight text-[var(--lp-ink)] sm:text-5xl">
            Create a live coding room.
          </h1>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-[var(--lp-ink-soft)]">
            Phase 1 starts with the room interface: shareable link, role-based join, meeting controls, chat, shared IDE, and shared whiteboard.
          </p>

          <div className="mt-8 space-y-4">
            <label className="block">
              <span className="text-sm text-[var(--lp-ink-soft)]">Interview title</span>
              <input
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                className="mt-2 h-12 w-full rounded-xl border border-[var(--lp-line)] bg-white px-4 text-sm text-[var(--lp-ink)] outline-none transition focus:border-[var(--lp-accent)] focus:ring-2 focus:ring-[var(--lp-accent)]/15"
              />
            </label>
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="block">
                <span className="text-sm text-[var(--lp-ink-soft)]">Role</span>
                <input
                  value={role}
                  onChange={(event) => setRole(event.target.value)}
                  className="mt-2 h-12 w-full rounded-xl border border-[var(--lp-line)] bg-white px-4 text-sm text-[var(--lp-ink)] outline-none transition focus:border-[var(--lp-accent)] focus:ring-2 focus:ring-[var(--lp-accent)]/15"
                />
              </label>
              <label className="block">
                <span className="text-sm text-[var(--lp-ink-soft)]">Team</span>
                <input
                  value={team}
                  onChange={(event) => setTeam(event.target.value)}
                  className="mt-2 h-12 w-full rounded-xl border border-[var(--lp-line)] bg-white px-4 text-sm text-[var(--lp-ink)] outline-none transition focus:border-[var(--lp-accent)] focus:ring-2 focus:ring-[var(--lp-accent)]/15"
                />
              </label>
            </div>
            <button
              type="button"
              onClick={() => setAiEnabled((value) => !value)}
              className={cn(
                'flex w-full items-start gap-3 rounded-2xl border p-4 text-left transition',
                aiEnabled
                  ? 'border-[var(--lp-accent)] bg-[var(--lp-accent)]/10'
                  : 'border-[var(--lp-line)] bg-white hover:bg-[var(--lp-surface)]'
              )}
            >
              <span className={cn(
                'mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full',
                aiEnabled ? 'bg-[var(--lp-accent)] text-white' : 'bg-[var(--lp-surface)] text-[var(--lp-ink-soft)]'
              )}>
                <Icon name="Sparkles" size={18} />
              </span>
              <span className="min-w-0">
                <span className="block text-sm text-[var(--lp-ink)]">Enable candidate AI help</span>
                <span className="mt-1 block text-xs leading-relaxed text-[var(--lp-ink-soft)]">
                  Candidate gets an assistant chat during the room. The full AI conversation is visible to the interviewer.
                </span>
              </span>
            </button>
          </div>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <Button
              onClick={handleCreate}
              size="lg"
              className="h-12 rounded-full bg-[var(--lp-accent)] px-8 text-sm text-white hover:bg-[var(--lp-accent)]/90"
              iconName="Link"
              iconPosition="left"
            >
              Generate interview link
            </Button>
            <Button
              asChild
              variant="outline"
              size="lg"
              className="h-12 rounded-full border-[var(--lp-line)] bg-white px-8 text-sm text-[var(--lp-ink)] hover:bg-[var(--lp-surface)]"
            >
              <Link to="/for-businesses#interview-ide">View product section</Link>
            </Button>
          </div>
        </section>

        <section className="overflow-hidden rounded-2xl border border-[var(--lp-line)] bg-[var(--lp-contrast-bg)] text-[var(--lp-contrast-text)] shadow-2xl shadow-black/10">
          <div className="flex h-14 items-center justify-between border-b border-white/10 px-5">
            <div>
              <p className="font-mono text-[10px] tracking-[0.2em] text-white/40">ROOM PREVIEW</p>
              <p className="mt-1 text-sm text-white/70">Meet plus IDE plus board</p>
            </div>
            <span className="rounded-full bg-[var(--lp-accent)]/15 px-3 py-1 text-xs text-[var(--lp-accent)]">
              {aiEnabled ? 'AI help enabled' : 'AI help optional'}
            </span>
          </div>
          <div className="grid gap-px bg-white/10 md:grid-cols-[1fr_0.72fr]">
            <div className="bg-[#101217] p-4">
              <div className="grid grid-cols-2 gap-3">
                {['Interviewer', 'Candidate', 'Panelist', 'Observer'].map((name, index) => (
                  <div key={name} className="aspect-video rounded-xl border border-white/10 bg-white/[0.04] p-3">
                    <div className="flex h-full flex-col justify-between">
                      <div className="flex justify-end">
                        <span className="rounded-full bg-black/35 px-2 py-1 text-[10px] text-white/55">
                          {index === 1 ? 'coding' : 'watching'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[var(--lp-accent)] text-xs text-white">
                          {getInitials(name)}
                        </span>
                        <span className="text-sm text-white/80">{name}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-[#0d0f12] p-4">
              <div className="space-y-3">
                {[
                  ['Code2', 'Shared IDE'],
                  ['PenTool', 'Shared whiteboard'],
                  ['MousePointer2', 'Live pointers'],
                  ['MessageSquare', 'Room chat'],
                ].map(([icon, label]) => (
                  <div key={label} className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-3 py-3 text-sm text-white/70">
                    <Icon name={icon} size={16} className="text-[var(--lp-accent)]" />
                    {label}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

const JoinInterviewScreen = ({ room, onJoin }) => {
  const location = useLocation();
  const createdByCurrentUser = new URLSearchParams(location.search).get('created') === '1';
  const [name, setName] = useState(createdByCurrentUser ? 'Interviewer' : 'Candidate');
  const [role, setRole] = useState(createdByCurrentUser ? 'interviewer' : 'candidate');
  const [micOn, setMicOn] = useState(true);
  const [cameraOn, setCameraOn] = useState(true);
  const [proctorConsentAccepted, setProctorConsentAccepted] = useState(role === 'interviewer');
  const [environmentStatus, setEnvironmentStatus] = useState(() => ({
    online: typeof navigator === 'undefined' ? true : navigator.onLine,
    workspaceOk: typeof window === 'undefined' ? true : window.innerWidth >= 900 && window.innerHeight >= 620,
  }));

  const { stream: previewStream, error: previewMediaError } = useLocalMedia({ micOn, cameraOn });
  const micLevel = useAudioLevel(previewStream, micOn);

  const shareUrl = typeof window !== 'undefined' ? `${window.location.origin}/for-businesses/interviews/${room.id}` : '';

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
    } catch {
      // Clipboard permissions can be blocked in dev previews.
    }
  };

  const requiresProctorConsent = role === 'candidate' && !proctorConsentAccepted;
  const requiresPreflight = role === 'candidate';
  const hasMediaApi = Boolean(navigator.mediaDevices?.getUserMedia);
  const cameraReady = cameraOn && Boolean(previewStream?.getVideoTracks?.().some((track) => track.readyState === 'live' && track.enabled));
  const micReady = micOn && Boolean(previewStream?.getAudioTracks?.().some((track) => track.readyState === 'live' && track.enabled));
  const preflightPassed = !requiresPreflight || (
    window.isSecureContext
    && hasMediaApi
    && cameraReady
    && micReady
    && environmentStatus.online
  );

  useEffect(() => {
    const updateEnvironment = () => {
      setEnvironmentStatus({
        online: navigator.onLine,
        workspaceOk: window.innerWidth >= 900 && window.innerHeight >= 620,
      });
    };

    updateEnvironment();
    window.addEventListener('resize', updateEnvironment);
    window.addEventListener('online', updateEnvironment);
    window.addEventListener('offline', updateEnvironment);
    return () => {
      window.removeEventListener('resize', updateEnvironment);
      window.removeEventListener('online', updateEnvironment);
      window.removeEventListener('offline', updateEnvironment);
    };
  }, []);

  const mediaStatusItems = [
    { label: cameraReady ? 'Camera live' : cameraOn ? 'Camera needed' : 'Camera off', status: cameraReady ? 'pass' : 'fail', icon: cameraReady ? 'Video' : 'VideoOff' },
    { label: micReady ? 'Mic live' : micOn ? 'Mic needed' : 'Mic off', status: micReady ? 'pass' : 'fail', icon: micReady ? 'Mic' : 'MicOff' },
    { label: environmentStatus.online ? 'Online' : 'Offline', status: environmentStatus.online ? 'pass' : 'fail', icon: 'Wifi' },
    { label: environmentStatus.workspaceOk ? 'Workspace ready' : 'Small window', status: environmentStatus.workspaceOk ? 'pass' : 'warn', icon: 'Monitor' },
  ];

  const previewHint = previewMediaError
    || (!window.isSecureContext || !hasMediaApi
      ? 'Camera and microphone need localhost or HTTPS in a supported browser.'
      : role === 'candidate' && !preflightPassed
        ? 'Camera and microphone must be live before joining as candidate.'
        : 'Media preview is ready.');

  return (
    <div className="landing-page min-h-screen bg-white text-[var(--lp-ink)]">
      {requiresProctorConsent && (
        <ProctorConsentBanner onAccept={() => setProctorConsentAccepted(true)} />
      )}
      <main className="mx-auto grid min-h-screen max-w-7xl items-center gap-10 px-5 py-10 sm:px-6 lg:grid-cols-[0.95fr_1.05fr] lg:px-8">
        <section>
          <p className="font-mono text-[11px] tracking-[0.25em] text-[var(--lp-accent)]">JOIN ROOM</p>
          <h1 className="mt-4 text-4xl leading-tight tracking-tight text-[var(--lp-ink)] sm:text-5xl">{room.title}</h1>
          <p className="mt-4 max-w-xl text-base leading-relaxed text-[var(--lp-ink-soft)]">
            Join as interviewer or candidate. The room starts in Meet mode, then the interviewer can open the shared IDE or board when ready.
          </p>

          <div className="mt-8 rounded-2xl border border-[var(--lp-line)] bg-white p-5">
            <div className="flex flex-wrap items-center gap-3">
              <span className="rounded-full bg-[var(--lp-accent)]/10 px-3 py-1 text-xs text-[var(--lp-accent)]">{room.role}</span>
              <span className="rounded-full border border-[var(--lp-line)] px-3 py-1 text-xs text-[var(--lp-ink-soft)]">{room.team}</span>
              <span className="rounded-full border border-[var(--lp-line)] px-3 py-1 text-xs text-[var(--lp-ink-soft)]">{room.provider}</span>
            </div>
            <div className="mt-5 flex flex-col gap-3 sm:flex-row">
              <input
                readOnly
                value={shareUrl}
                className="h-11 flex-1 rounded-xl border border-[var(--lp-line)] bg-[var(--lp-surface)] px-4 font-mono text-xs text-[var(--lp-ink-soft)] outline-none"
              />
              <Button
                type="button"
                variant="outline"
                onClick={handleCopy}
                className="h-11 rounded-full border-[var(--lp-line)] bg-white px-5 text-sm text-[var(--lp-ink)] hover:bg-[var(--lp-surface)]"
                iconName="Copy"
                iconPosition="left"
              >
                Copy link
              </Button>
            </div>
          </div>
        </section>

        <section className="rounded-2xl border border-[var(--lp-line)] bg-white p-5 shadow-2xl shadow-black/5">
          <div className="relative aspect-video overflow-hidden rounded-xl border border-[var(--lp-line)] bg-[var(--lp-contrast-bg)] text-[var(--lp-contrast-text)]">
            {cameraReady && previewStream ? (
              <>
                <VideoElement stream={previewStream} muted />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-black/35" />
              </>
            ) : (
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_30%,rgba(22,152,115,0.22),transparent_30%),linear-gradient(135deg,#070707,#111216)]" />
            )}

            <div className="relative flex h-full flex-col justify-between p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="rounded-full bg-black/45 px-3 py-1.5 text-[11px] text-white/70 backdrop-blur">
                  Pre-join media preview
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setMicOn((value) => !value)}
                    className={cn(
                      'flex h-10 w-10 items-center justify-center rounded-full border text-white transition backdrop-blur',
                      micOn ? 'border-white/15 bg-white/10' : 'border-red-400/40 bg-red-500/30'
                    )}
                    title={micOn ? 'Mute microphone' : 'Unmute microphone'}
                  >
                    <Icon name={micOn ? 'Mic' : 'MicOff'} size={16} />
                  </button>
                  <button
                    type="button"
                    onClick={() => setCameraOn((value) => !value)}
                    className={cn(
                      'flex h-10 w-10 items-center justify-center rounded-full border text-white transition backdrop-blur',
                      cameraOn ? 'border-white/15 bg-white/10' : 'border-red-400/40 bg-red-500/30'
                    )}
                    title={cameraOn ? 'Turn camera off' : 'Turn camera on'}
                  >
                    <Icon name={cameraOn ? 'Video' : 'VideoOff'} size={16} />
                  </button>
                </div>
              </div>

              {!cameraReady && (
                <div className="flex flex-1 flex-col items-center justify-center gap-4 text-center">
                  <div className="flex h-24 w-24 items-center justify-center rounded-full bg-[var(--lp-accent)] text-3xl text-white">
                    {getInitials(name)}
                  </div>
                  <div>
                    <p className="text-lg text-white">{name || 'Your name'}</p>
                    <p className="text-sm capitalize text-white/45">{role}</p>
                  </div>
                </div>
              )}

              <div className="space-y-3">
                <div className="rounded-2xl border border-white/10 bg-black/45 p-3 backdrop-blur">
                  <div className="mb-2 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 text-sm text-white">
                      <Icon name={micOn ? 'Mic' : 'MicOff'} size={15} />
                      <span>Audio intensity</span>
                    </div>
                    <span className="font-mono text-xs text-white/60">{micLevel}%</span>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-white/15">
                    <div
                      className={cn(
                        'h-full rounded-full transition-all duration-150',
                        micReady ? 'bg-[var(--lp-accent)]' : 'bg-red-400'
                      )}
                      style={{ width: `${micReady ? Math.max(6, micLevel) : 6}%` }}
                    />
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  {mediaStatusItems.map((item) => (
                    <span
                      key={item.label}
                      className={cn(
                        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] backdrop-blur',
                        item.status === 'pass'
                          ? 'border-emerald-300/30 bg-emerald-400/15 text-emerald-100'
                          : item.status === 'warn'
                            ? 'border-amber-300/30 bg-amber-400/15 text-amber-100'
                            : 'border-red-300/30 bg-red-400/15 text-red-100'
                      )}
                    >
                      <Icon name={item.icon} size={12} />
                      {item.label}
                    </span>
                  ))}
                </div>

                <p className="text-xs leading-relaxed text-white/65">{previewHint}</p>
              </div>
            </div>
          </div>

          <div className="mt-5 space-y-4">
            <label className="block">
              <span className="text-sm text-[var(--lp-ink-soft)]">Your name</span>
              <input
                value={name}
                onChange={(event) => setName(event.target.value)}
                className="mt-2 h-12 w-full rounded-xl border border-[var(--lp-line)] bg-white px-4 text-sm text-[var(--lp-ink)] outline-none transition focus:border-[var(--lp-accent)] focus:ring-2 focus:ring-[var(--lp-accent)]/15"
              />
            </label>

            <div>
              <span className="text-sm text-[var(--lp-ink-soft)]">Join as</span>
              <div className="mt-2 grid grid-cols-2 gap-3">
                {[
                  ['interviewer', 'Interviewer', 'Can select questions and lead the room'],
                  ['candidate', 'Candidate', 'Can code, draw, and chat'],
                ].map(([value, label, description]) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => {
                      setRole(value);
                      setProctorConsentAccepted(value === 'interviewer');
                    }}
                    className={cn(
                      'rounded-xl border p-4 text-left transition',
                      role === value
                        ? 'border-[var(--lp-accent)] bg-[var(--lp-accent)]/10 text-[var(--lp-ink)]'
                        : 'border-[var(--lp-line)] bg-white text-[var(--lp-ink)] hover:bg-[var(--lp-surface)]'
                    )}
                  >
                    <span className="block text-sm">{label}</span>
                    <span className="mt-1 block text-xs leading-relaxed text-[var(--lp-ink-soft)]">{description}</span>
                  </button>
                ))}
              </div>
            </div>

            <Button
              onClick={() => onJoin({ participantId: getParticipantId(room.id), name: name || 'Guest', role, micOn, cameraOn, proctorConsentAccepted: role === 'interviewer' || proctorConsentAccepted })}
              disabled={requiresProctorConsent || !preflightPassed}
              size="lg"
              className="h-12 w-full rounded-full bg-[var(--lp-accent)] text-sm text-white hover:bg-[var(--lp-accent)]/90 disabled:cursor-not-allowed disabled:opacity-50"
              iconName="LogIn"
              iconPosition="left"
            >
              Join interview room
            </Button>
          </div>
        </section>
      </main>
    </div>
  );
};

const VideoElement = ({ stream, muted = false }) => {
  const videoRef = useRef(null);

  useEffect(() => {
    if (videoRef.current && stream) {
      videoRef.current.srcObject = stream;
    }
  }, [stream]);

  return (
    <video
      ref={videoRef}
      autoPlay
      playsInline
      muted={muted}
      className="absolute inset-0 h-full w-full object-cover"
    />
  );
};

const VideoTile = ({ participant, stream, large = false, full = false, floating = false }) => {
  const showVideo = stream && participant.cameraOn;

  return (
    <div className={cn(
      'relative overflow-hidden border border-[var(--lp-line)] bg-[var(--lp-contrast-bg)] text-white',
      floating
        ? 'h-32 w-52 rounded-xl shadow-2xl shadow-black/20 sm:h-36 sm:w-60'
        : 'rounded-2xl',
      full ? 'h-full min-h-0 flex-1' : large ? 'min-h-[280px]' : !floating && 'min-h-[170px]'
    )}>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(22,152,115,0.22),transparent_28%),linear-gradient(135deg,#111,#050505)]" />
      {showVideo && <VideoElement stream={stream} muted={participant.isCurrent} />}
      {showVideo && <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/5 to-black/25" />}
      <div className={cn('relative flex h-full flex-col justify-between', floating ? 'p-3' : 'p-4')}>
        <div className="flex items-center justify-between">
          <span className="rounded-full bg-black/35 px-2 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-white/60">
            {participant.role}
          </span>
          <div className="flex gap-1.5">
            <span className={cn('flex items-center justify-center rounded-full', floating ? 'h-6 w-6' : 'h-7 w-7', participant.micOn ? 'bg-white/10' : 'bg-red-500/25')}>
              <Icon name={participant.micOn ? 'Mic' : 'MicOff'} size={13} />
            </span>
            <span className={cn('flex items-center justify-center rounded-full', floating ? 'h-6 w-6' : 'h-7 w-7', participant.cameraOn ? 'bg-white/10' : 'bg-red-500/25')}>
              <Icon name={participant.cameraOn ? 'Video' : 'VideoOff'} size={13} />
            </span>
          </div>
        </div>

        <div className="flex flex-1 items-center justify-center">
          <div
            className={cn(
              'items-center justify-center rounded-full text-white',
              showVideo ? 'hidden' : 'flex',
              floating ? 'h-14 w-14 text-lg' : large ? 'h-28 w-28 text-4xl' : 'h-20 w-20 text-2xl'
            )}
            style={{ backgroundColor: participant.color }}
          >
            {getInitials(participant.name)}
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="min-w-0">
            <p className={cn('truncate text-white', floating ? 'text-xs' : 'text-sm')}>{participant.name}</p>
            <p className="truncate text-xs text-white/45">{participant.status}</p>
          </div>
          {participant.active && !floating && (
            <span className="flex items-center gap-1.5 rounded-full bg-[var(--lp-accent)]/15 px-2 py-1 text-xs text-[var(--lp-accent)]">
              <span className="h-1.5 w-1.5 rounded-full bg-[var(--lp-accent)]" />
              speaking
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

const IconControl = ({ icon, active, danger, label, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    onPointerUp={(event) => event.currentTarget.blur()}
    title={label}
    className={cn(
      'flex h-11 w-11 items-center justify-center rounded-full border transition',
      danger
        ? 'border-red-500 bg-red-500 text-white hover:bg-red-600'
        : active
          ? 'border-[var(--lp-accent)] bg-[var(--lp-accent)] text-white hover:bg-[var(--lp-accent)]/90'
          : 'border-white/10 bg-white/5 text-white/75 hover:bg-white/10 hover:text-white'
    )}
  >
    <Icon name={icon} size={18} />
    <span className="sr-only">{label}</span>
  </button>
);

const FloatingVideoStrip = ({ participants, getParticipantStream }) => {
  const stripRef = useRef(null);
  const dragRef = useRef(null);
  const [position, setPosition] = useState(null);
  const visibleParticipants = participants.slice(0, 2);

  if (visibleParticipants.length === 0) return null;

  const clampPosition = (x, y) => {
    const strip = stripRef.current;
    const parent = strip?.parentElement;
    if (!strip || !parent) return { x, y };

    const parentRect = parent.getBoundingClientRect();
    const stripRect = strip.getBoundingClientRect();
    return {
      x: Math.max(8, Math.min(parentRect.width - stripRect.width - 8, x)),
      y: Math.max(72, Math.min(parentRect.height - stripRect.height - 84, y)),
    };
  };

  const handleDragStart = (event) => {
    if (event.button !== undefined && event.button !== 0) return;
    const strip = stripRef.current;
    const parent = strip?.parentElement;
    if (!strip || !parent) return;

    const parentRect = parent.getBoundingClientRect();
    const stripRect = strip.getBoundingClientRect();
    const current = position || {
      x: stripRect.left - parentRect.left,
      y: stripRect.top - parentRect.top,
    };

    dragRef.current = {
      offsetX: event.clientX - parentRect.left - current.x,
      offsetY: event.clientY - parentRect.top - current.y,
    };
    setPosition(clampPosition(current.x, current.y));
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const handleDragMove = (event) => {
    if (!dragRef.current) return;
    const parent = stripRef.current?.parentElement;
    if (!parent) return;

    const parentRect = parent.getBoundingClientRect();
    setPosition(clampPosition(
      event.clientX - parentRect.left - dragRef.current.offsetX,
      event.clientY - parentRect.top - dragRef.current.offsetY
    ));
  };

  const handleDragEnd = (event) => {
    dragRef.current = null;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
  };

  return (
    <div
      ref={stripRef}
      className={cn(
        'pointer-events-auto absolute z-30 flex flex-col items-end gap-2',
        !position && 'bottom-24 right-4'
      )}
      style={position ? { left: `${position.x}px`, top: `${position.y}px` } : undefined}
    >
      <div
        role="button"
        tabIndex={0}
        className="flex cursor-grab select-none items-center gap-2 rounded-full border border-white/10 bg-[#1f2124]/90 px-3 py-1.5 text-xs font-medium text-white/80 shadow-lg active:cursor-grabbing"
        title="Drag video"
        onPointerDown={handleDragStart}
        onPointerMove={handleDragMove}
        onPointerUp={handleDragEnd}
        onPointerCancel={handleDragEnd}
      >
        <Icon name="GripHorizontal" size={14} />
        Video
      </div>
      {visibleParticipants.map((participant) => (
        <VideoTile
          key={participant.id}
          participant={participant}
          stream={getParticipantStream(participant)}
          floating
        />
      ))}
    </div>
  );
};

const PointerLayer = ({ participants, currentPointer }) => {
  const visiblePointers = participants.map((participant) => (
    participant.isCurrent
      ? { ...participant, pointer: currentPointer }
      : participant
  )).filter((participant) => participant.pointer);

  return (
    <div className="pointer-events-none absolute inset-0 z-20">
      {visiblePointers.map((participant) => (
        <div
          key={participant.id}
          className="absolute flex items-center gap-1.5 rounded-full border border-black/10 bg-white px-2 py-1 text-[11px] shadow-lg"
          style={{
            left: `${participant.pointer.x}%`,
            top: `${participant.pointer.y}%`,
            color: participant.color,
            transform: 'translate(8px, 8px)',
          }}
        >
          <Icon name="MousePointer2" size={13} />
          <span className="text-[var(--lp-ink)]">{participant.isCurrent ? 'You' : participant.name}</span>
        </div>
      ))}
    </div>
  );
};

const QuestionPanel = ({ selectedQuestion, selectedQuestionId, canSelect, onSelect, style, className }) => (
  <aside style={style} className={cn("flex min-h-0 flex-col border-r border-[var(--lp-line)] bg-white", className)}>
    <div className="border-b border-[var(--lp-line)] px-4 py-3">
      {canSelect && (
        <label className="block">
          <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-[var(--lp-ink-soft)]">Interviewer question picker</span>
          <select
            value={selectedQuestionId}
            onChange={(event) => onSelect(event.target.value)}
            className="mt-2 h-10 w-full rounded-xl border border-[var(--lp-line)] bg-white px-3 text-sm font-medium text-[var(--lp-ink)] shadow-sm outline-none focus:border-[var(--lp-accent)]"
          >
            <option value="">No question selected</option>
            {questionBank.map((question) => (
              <option key={question.id} value={question.id}>{question.title}</option>
            ))}
          </select>
        </label>
      )}
    </div>

    <div className="min-h-0 flex-1 overflow-y-auto p-4">
      {!selectedQuestion ? (
        <div className="flex h-full min-h-[340px] flex-col items-center justify-center rounded-xl border border-dashed border-[var(--lp-line)] bg-white p-6 text-center">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl border border-[var(--lp-line)] text-[var(--lp-ink-soft)]">
            <Icon name="FileQuestion" size={22} />
          </span>
          <p className="mt-5 text-sm font-medium text-[var(--lp-ink)]">No coding question is selected yet.</p>
          <p className="mt-3 max-w-xs text-sm leading-relaxed text-[var(--lp-ink-soft)]">
            The interviewer can choose one when the candidate is ready.
          </p>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-2">
            {selectedQuestion.difficulty && (
              <span className="rounded-full bg-[var(--lp-accent)]/10 px-3 py-1 text-xs font-medium text-[var(--lp-accent)]">
                {selectedQuestion.difficulty}
              </span>
            )}
            {selectedQuestion.duration && (
              <span className="rounded-full border border-[var(--lp-line)] px-3 py-1 text-xs text-[var(--lp-ink-soft)]">
                {selectedQuestion.duration}
              </span>
            )}
          </div>
          <p className="mt-4 text-sm leading-relaxed text-[var(--lp-ink)]">{selectedQuestion.prompt}</p>
          <div className="mt-5 flex flex-wrap gap-2">
            {selectedQuestion.topics.map((topic) => (
              <span key={topic} className="rounded-full border border-[var(--lp-line)] px-3 py-1 text-xs text-[var(--lp-ink-soft)]">{topic}</span>
            ))}
          </div>

          <div className="mt-6 space-y-3">
            <h3 className="text-sm text-[var(--lp-ink)]">Examples</h3>
            {selectedQuestion.examples.map((example, index) => (
              <div key={`${example.input}-${index}`} className="rounded-xl border border-[var(--lp-line)] bg-[var(--lp-surface)] p-3 font-mono text-xs text-[var(--lp-ink)]">
                <p><span className="text-[var(--lp-ink-soft)]">Input:</span> {example.input}</p>
                <p className="mt-1"><span className="text-[var(--lp-ink-soft)]">Output:</span> {example.output}</p>
              </div>
            ))}
          </div>

          <div className="mt-6 space-y-2">
            <h3 className="text-sm text-[var(--lp-ink)]">Constraints</h3>
            {selectedQuestion.constraints.map((constraint) => (
              <div key={constraint} className="flex gap-2 text-sm leading-relaxed text-[var(--lp-ink-soft)]">
                <Icon name="Check" size={15} className="mt-0.5 shrink-0 text-[var(--lp-accent)]" />
                {constraint}
              </div>
            ))}
          </div>

          {selectedQuestion.schema?.length > 0 && (
            <div className="mt-6 space-y-3">
              <h3 className="text-sm text-[var(--lp-ink)]">Database schema</h3>
              {selectedQuestion.schema.map((table) => (
                <div key={table.table} className="rounded-xl border border-[var(--lp-line)] bg-white p-3">
                  <p className="font-mono text-xs text-[var(--lp-ink)]">{table.table}</p>
                  <div className="mt-2 space-y-1">
                    {table.columns.map((column) => (
                      <p key={column} className="font-mono text-[11px] text-[var(--lp-ink-soft)]">{column}</p>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  </aside>
);

const IdeWorkspace = ({
  code,
  language,
  participants,
  pointer,
  selectedQuestion,
  selectedQuestionId,
  canSelect,
  editorTheme,
  onCodeChange,
  onLanguageChange,
  onPointerMove,
  onQuestionSelect,
  currentParticipantId,
  codeSelection,
  onCodeSelectionChange,
  customInput,
  onCustomInputChange,
  runResult,
  isRunningCode,
  onRunCode,
  onRunTests,
  onToggleEditorTheme,
  onEditorDomReady,
  onInternalCopy,
}) => {
  const editorRef = useRef(null);
  const monacoRef = useRef(null);
  const selectionDisposableRef = useRef(null);
  const selectionDecorationsRef = useRef([]);
  const lastSelectionSignatureRef = useRef('');

  const [sidebarWidth, setSidebarWidth] = useState(320);
  const [consoleHeight, setConsoleHeight] = useState(220);
  const [inputWidth, setInputWidth] = useState(430);
  const isFrontendPreview = isFrontendLanguage(language) || selectedQuestion?.type === 'frontend';
  const isSqlMode = isSqlLanguage(language) || selectedQuestion?.type === 'sql';
  const frontendPreviewSrcDoc = useMemo(
    () => buildFrontendPreviewSrcDoc(code, language === 'html' ? 'html' : 'react'),
    [code, language]
  );

  const startResizingSidebar = useCallback((mouseDownEvent) => {
    mouseDownEvent.preventDefault();
    const startX = mouseDownEvent.clientX;
    const startWidth = sidebarWidth;

    const doDrag = (mouseMoveEvent) => {
      const newWidth = Math.max(280, Math.min(460, startWidth + (mouseMoveEvent.clientX - startX)));
      setSidebarWidth(newWidth);
    };

    const stopDrag = () => {
      document.removeEventListener('mousemove', doDrag);
      document.removeEventListener('mouseup', stopDrag);
    };

    document.addEventListener('mousemove', doDrag);
    document.addEventListener('mouseup', stopDrag);
  }, [sidebarWidth]);

  const startResizingConsole = useCallback((mouseDownEvent) => {
    mouseDownEvent.preventDefault();
    const startY = mouseDownEvent.clientY;
    const startHeight = consoleHeight;

    const doDrag = (mouseMoveEvent) => {
      const newHeight = Math.max(120, Math.min(600, startHeight - (mouseMoveEvent.clientY - startY)));
      setConsoleHeight(newHeight);
    };

    const stopDrag = () => {
      document.removeEventListener('mousemove', doDrag);
      document.removeEventListener('mouseup', stopDrag);
    };

    document.addEventListener('mousemove', doDrag);
    document.addEventListener('mouseup', stopDrag);
  }, [consoleHeight]);

  const startResizingInput = useCallback((mouseDownEvent) => {
    mouseDownEvent.preventDefault();
    const startX = mouseDownEvent.clientX;
    const startWidth = inputWidth;

    const doDrag = (mouseMoveEvent) => {
      const newWidth = Math.max(220, Math.min(640, startWidth + (mouseMoveEvent.clientX - startX)));
      setInputWidth(newWidth);
    };

    const stopDrag = () => {
      document.removeEventListener('mousemove', doDrag);
      document.removeEventListener('mouseup', stopDrag);
    };

    document.addEventListener('mousemove', doDrag);
    document.addEventListener('mouseup', stopDrag);
  }, [inputWidth]);

  useEffect(() => () => {
    selectionDisposableRef.current?.dispose?.();
  }, []);

  useEffect(() => {
    const editor = editorRef.current;
    const monaco = monacoRef.current;
    if (!editor || !monaco) return;

    const isRemoteSelection = codeSelection?.range && codeSelection.participantId !== currentParticipantId;
    const decorations = isRemoteSelection ? [{
      range: new monaco.Range(
        codeSelection.range.startLineNumber,
        codeSelection.range.startColumn,
        codeSelection.range.endLineNumber,
        codeSelection.range.endColumn
      ),
      options: {
        className: 'business-remote-code-selection',
        hoverMessage: { value: `${codeSelection.name || 'Participant'} selected this code` },
        stickiness: monaco.editor.TrackedRangeStickiness.NeverGrowsWhenTypingAtEdges,
      },
    }] : [];

    selectionDecorationsRef.current = editor.deltaDecorations(selectionDecorationsRef.current, decorations);
  }, [codeSelection, currentParticipantId]);

  const renderRunResult = () => {
    if (isFrontendPreview) {
      return (
        <div className="flex h-full min-h-0 flex-col">
          <div className="mb-2 flex items-center justify-between gap-3">
            <div>
              <p className="text-xs text-[var(--lp-ink)]">Live frontend preview</p>
              <p className="text-[11px] text-[var(--lp-ink-soft)]">Updates as the shared editor changes</p>
            </div>
            <span className="rounded-full bg-[var(--lp-accent)]/10 px-2.5 py-1 text-[10px] text-[var(--lp-accent)]">
              {language === 'html' ? 'HTML/CSS' : 'React'}
            </span>
          </div>
          <iframe
            title="Frontend preview"
            sandbox="allow-scripts"
            srcDoc={frontendPreviewSrcDoc}
            className="min-h-0 flex-1 rounded-xl border border-[var(--lp-line)] bg-white"
          />
        </div>
      );
    }

    if (isRunningCode) {
      return (
        <div className="flex h-full items-center justify-center gap-3 text-sm text-[var(--lp-ink-soft)]">
          <span className="h-4 w-4 animate-spin rounded-full border-2 border-[var(--lp-accent)] border-t-transparent" />
          Running code...
        </div>
      );
    }

    if (runResult?.mode === 'sql') {
      return (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-xs text-[var(--lp-ink-soft)]">SQL validation</p>
            <span className={cn(
              'rounded-full px-2.5 py-1 text-[10px]',
              runResult.passed === runResult.total ? 'bg-[var(--lp-accent)]/10 text-[var(--lp-accent)]' : 'bg-amber-50 text-amber-700'
            )}>
              {runResult.passed}/{runResult.total} checks
            </span>
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            {runResult.checks?.map((check) => (
              <div key={check.label} className="flex items-center gap-2 rounded-xl border border-[var(--lp-line)] bg-white p-2 text-xs">
                <Icon name={check.passed ? 'CheckCircle2' : 'Circle'} size={14} className={check.passed ? 'text-[var(--lp-accent)]' : 'text-[var(--lp-ink-soft)]'} />
                <span className={check.passed ? 'text-[var(--lp-ink)]' : 'text-[var(--lp-ink-soft)]'}>{check.label}</span>
              </div>
            ))}
          </div>
          {runResult.rows?.length > 0 && (
            <div className="overflow-hidden rounded-xl border border-[var(--lp-line)]">
              <table className="w-full text-left text-xs">
                <thead className="bg-[var(--lp-surface)] text-[var(--lp-ink-soft)]">
                  <tr>
                    {runResult.columns.map((column) => (
                      <th key={column} className="px-3 py-2 font-medium">{column}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {runResult.rows.map((row, index) => (
                    <tr key={`${row.name}-${index}`} className="border-t border-[var(--lp-line)]">
                      {runResult.columns.map((column) => (
                        <td key={column} className="px-3 py-2 font-mono text-[var(--lp-ink)]">{row[column]}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      );
    }

    if (!runResult) {
      return (
        <div className="flex h-full flex-col items-center justify-center text-center text-sm text-[var(--lp-ink-soft)]">
          <Icon name={isSqlMode ? 'Database' : 'Terminal'} size={22} className="mb-2" />
          {isSqlMode ? 'Validate the SQL query to share checks and sample output.' : 'Run code or test cases to share the output with everyone.'}
        </div>
      );
    }

    if (runResult.mode === 'tests') {
      return (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-xs text-[var(--lp-ink-soft)]">Test results</p>
            <span className={cn(
              'rounded-full px-2.5 py-1 text-[10px]',
              runResult.passed === runResult.total ? 'bg-[var(--lp-accent)]/10 text-[var(--lp-accent)]' : 'bg-red-50 text-red-600'
            )}>
              {runResult.passed}/{runResult.total} passed
            </span>
          </div>
          {runResult.tests?.map((test, index) => (
            <div key={`${test.input}-${index}`} className="rounded-xl border border-[var(--lp-line)] bg-white p-3">
              <div className="flex items-center justify-between gap-3">
                <span className="text-xs text-[var(--lp-ink)]">Case {index + 1}</span>
                <span className={cn('text-xs', test.passed ? 'text-[var(--lp-accent)]' : 'text-red-600')}>
                  {test.passed ? 'Passed' : 'Failed'}
                </span>
              </div>
              <pre className="mt-2 whitespace-pre-wrap font-mono text-[11px] leading-relaxed text-[var(--lp-ink-soft)]">
{`Input: ${test.input}
Expected: ${test.expected}
Actual: ${test.actual || '(no output)'}`}
              </pre>
              {(test.stderr || test.error) && (
                <pre className="mt-2 whitespace-pre-wrap rounded-lg bg-red-50 p-2 font-mono text-[11px] text-red-700">
                  {test.stderr || test.error}
                </pre>
              )}
            </div>
          ))}
        </div>
      );
    }

    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-3">
          <p className="text-xs text-[var(--lp-ink-soft)]">Execution result</p>
          <span className="rounded-full bg-[var(--lp-surface)] px-2.5 py-1 text-[10px] text-[var(--lp-ink-soft)]">
            {runResult.status || 'Finished'}
          </span>
        </div>
        {runResult.stdout && (
          <div>
            <p className="mb-1 text-[10px] uppercase tracking-[0.16em] text-[var(--lp-ink-soft)]">Stdout</p>
            <pre className="whitespace-pre-wrap rounded-xl bg-[#111216] p-3 font-mono text-xs leading-relaxed text-white">{runResult.stdout}</pre>
          </div>
        )}
        {(runResult.stderr || runResult.compile_output || runResult.error) && (
          <div>
            <p className="mb-1 text-[10px] uppercase tracking-[0.16em] text-red-500">Error</p>
            <pre className="whitespace-pre-wrap rounded-xl bg-red-50 p-3 font-mono text-xs leading-relaxed text-red-700">
              {runResult.stderr || runResult.compile_output || runResult.error}
            </pre>
          </div>
        )}
        {!runResult.stdout && !runResult.stderr && !runResult.compile_output && !runResult.error && (
          <p className="text-sm text-[var(--lp-ink-soft)]">Program finished with no output.</p>
        )}
      </div>
    );
  };

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden bg-white lg:flex-row">
      <style>{`
        .business-remote-code-selection {
          background: rgba(22, 152, 115, 0.24);
          border-bottom: 2px solid rgba(22, 152, 115, 0.75);
        }
      `}</style>
      <QuestionPanel
        selectedQuestion={selectedQuestion}
        selectedQuestionId={selectedQuestionId}
        canSelect={canSelect}
        onSelect={onQuestionSelect}
        style={{ '--business-sidebar-width': `${sidebarWidth}px` }}
        className="w-full lg:w-[var(--business-sidebar-width)]"
      />
      <div
        className="group relative z-30 hidden w-1 cursor-col-resize items-center justify-center self-stretch bg-transparent lg:flex"
        onMouseDown={startResizingSidebar}
      >
        <div className="h-full w-[1px] bg-[var(--lp-line)] transition-all group-hover:w-[2px] group-hover:bg-[var(--lp-accent)]" />
      </div>
      <section className="flex min-h-0 flex-1 flex-col bg-white min-w-0">
        <div className="flex min-h-14 shrink-0 flex-wrap items-center justify-between gap-3 border-b border-[var(--lp-line)] bg-white px-4 py-2">
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={language}
              onChange={(event) => onLanguageChange(event.target.value)}
              className="h-10 rounded-xl border border-[var(--lp-line)] bg-white px-3 text-sm font-medium text-[var(--lp-ink)] shadow-sm outline-none focus:border-[var(--lp-accent)]"
            >
              {languages.map((item) => (
                <option key={item.value} value={item.value}>{item.label}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={onRunCode}
              disabled={isRunningCode || !code.trim()}
              className="flex h-10 items-center gap-2 rounded-xl border border-[var(--lp-line)] bg-white px-4 text-sm font-medium text-[var(--lp-ink)] shadow-sm transition hover:bg-[var(--lp-surface)] disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Icon name={isSqlMode ? 'Database' : isFrontendPreview ? 'RefreshCw' : 'Play'} size={15} />
              {isSqlMode ? 'Validate' : isFrontendPreview ? 'Refresh' : 'Run'}
            </button>
            {!isFrontendPreview && !isSqlMode && (
              <button
                type="button"
                onClick={onRunTests}
                disabled={isRunningCode || !selectedQuestion?.examples?.length}
                className="flex h-10 items-center gap-2 rounded-xl border border-[var(--lp-line)] bg-white px-4 text-sm font-medium text-[var(--lp-ink)] shadow-sm transition hover:bg-[var(--lp-surface)] disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Icon name="CheckSquare" size={15} />
                Tests
              </button>
            )}
            <span className="ml-1 hidden items-center gap-2 px-2 text-sm text-[var(--lp-ink-soft)] sm:flex">
              <span className="h-1.5 w-1.5 rounded-full bg-[var(--lp-accent)]" />
              Auto-saved
            </span>
          </div>
          <div className="hidden items-center gap-1 sm:flex">
            <button
              type="button"
              onClick={onToggleEditorTheme}
              className="flex h-9 w-9 items-center justify-center rounded-xl text-[var(--lp-ink-soft)] transition hover:bg-[var(--lp-surface)]"
              title="Toggle editor theme"
            >
              <Icon name={editorTheme === 'light' ? 'Sun' : 'Moon'} size={17} />
            </button>
          </div>
        </div>

        {codeSelection?.text && (
          <div className="flex shrink-0 items-center gap-2 border-b border-[var(--lp-line)] bg-[var(--lp-accent)]/5 px-4 py-2 text-xs text-[var(--lp-ink-soft)]">
            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: codeSelection.color || 'var(--lp-accent)' }} />
            <span className="text-[var(--lp-ink)]">{codeSelection.name || 'Participant'}</span>
            <span>selected lines {codeSelection.range?.startLineNumber}-{codeSelection.range?.endLineNumber}</span>
            <span className="truncate font-mono text-[var(--lp-ink-soft)]">{codeSelection.text.replace(/\s+/g, ' ').slice(0, 120)}</span>
          </div>
        )}

        <div className="relative min-h-0 flex-1" onMouseMove={onPointerMove}>
          <Editor
            height="100%"
            language={getEditorLanguage(language)}
            value={code}
            onChange={(value) => onCodeChange(value || '')}
            onMount={(editor, monaco) => {
              editorRef.current = editor;
              monacoRef.current = monaco;
              onEditorDomReady?.(editor.getDomNode());

              // Track copy-from-IDE natively without breaking system clipboard
              const domNode = editor.getDomNode();
              if (domNode) {
                domNode.addEventListener('copy', () => {
                  const selection = editor.getSelection();
                  const model = editor.getModel();
                  if (selection && model && !selection.isEmpty()) {
                    const text = model.getValueInRange(selection);
                    onInternalCopy?.(text);
                  }
                });
              }

              selectionDisposableRef.current?.dispose?.();
              selectionDisposableRef.current = editor.onDidChangeCursorSelection((event) => {
                const model = editor.getModel();
                const selection = event.selection;
                if (!model || !selection) return;

                const selectedText = selection.isEmpty() ? '' : model.getValueInRange(selection);
                const nextSelection = selectedText ? {
                  range: {
                    startLineNumber: selection.startLineNumber,
                    startColumn: selection.startColumn,
                    endLineNumber: selection.endLineNumber,
                    endColumn: selection.endColumn,
                  },
                  text: selectedText.slice(0, 2000),
                } : null;

                const signature = JSON.stringify(nextSelection);
                if (signature === lastSelectionSignatureRef.current) return;
                lastSelectionSignatureRef.current = signature;
                onCodeSelectionChange(nextSelection);
              });
            }}
            theme={editorTheme}
            options={{
              fontSize: 14,
              fontFamily: "'Fira Code', 'Cascadia Code', 'Consolas', monospace",
              minimap: { enabled: false },
              scrollBeyondLastLine: false,
              wordWrap: 'on',
              lineHeight: 24,
              automaticLayout: true,
              padding: { top: 18, bottom: 18 },
              renderLineHighlight: 'all',
            }}
            loading={
              <div className="flex h-full items-center justify-center bg-white text-sm text-[var(--lp-ink-soft)]">
                Loading shared editor
              </div>
            }
          />
          <PointerLayer participants={participants} currentPointer={pointer} />
          <div className="pointer-events-none absolute bottom-4 left-4 z-10 hidden flex-wrap gap-2 xl:flex">
            {participants.map((participant) => (
              <span key={participant.id} className="rounded-full border border-[var(--lp-line)] bg-white px-2.5 py-1 text-xs text-[var(--lp-ink-soft)] shadow-sm">
                <span className="mr-1.5 inline-block h-2 w-2 rounded-full" style={{ backgroundColor: participant.color }} />
                {participant.name} {participant.isCurrent ? 'editing' : 'viewing'}
              </span>
            ))}
          </div>
        </div>

        <div
          className="group relative z-30 flex h-1 cursor-row-resize items-center justify-center bg-transparent shrink-0"
          onMouseDown={startResizingConsole}
        >
          <div className="h-[1px] w-full bg-[var(--lp-line)] transition-all group-hover:h-[2px] group-hover:bg-[var(--lp-accent)]" />
        </div>

        <div
          style={{ height: `${consoleHeight}px` }}
          className="flex min-h-0 shrink-0 flex-col border-t border-[var(--lp-line)] bg-white md:flex-row"
        >
          {!isFrontendPreview && !isSqlMode && (
            <>
              <div
                style={{ width: `${inputWidth}px` }}
                className="flex min-h-0 w-full shrink-0 flex-col p-5 md:w-auto"
              >
                <label className="font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-[var(--lp-ink-soft)]">Custom input</label>
                <textarea
                  value={customInput}
                  onChange={(event) => onCustomInputChange(event.target.value)}
                  placeholder="stdin for Run code"
                  className="mt-3 flex-1 resize-none rounded-xl border border-[var(--lp-line)] bg-white p-4 font-mono text-sm text-[var(--lp-ink)] outline-none transition-all hover:bg-[var(--lp-surface)]/40 focus:border-[var(--lp-accent)] focus:bg-white"
                />
              </div>
              <div
                className="group relative z-30 hidden w-1 shrink-0 cursor-col-resize items-center justify-center self-stretch bg-transparent md:flex"
                onMouseDown={startResizingInput}
              >
                <div className="h-full w-[1px] bg-[var(--lp-line)] transition-all group-hover:w-[2px] group-hover:bg-[var(--lp-accent)]" />
              </div>
            </>
          )}
          <div className="flex min-h-0 flex-1 flex-col bg-white p-5">
            <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-[var(--lp-ink-soft)]">Output</p>
            <div className="mt-3 min-h-0 flex-1 overflow-y-auto rounded-xl bg-white">
              {renderRunResult()}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

const WhiteboardWorkspace = ({ roomId, participants, pointer, snapshot, onPointerMove, onSnapshotChange }) => {
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  // 'pencil' | 'colorPencil' | 'marker' | 'highlighter' | 'broadHighlighter' | 'eraser' | 'fill' | shapes | 'text'
  const [tool, setTool] = useState('pencil');
  const [color, setColor] = useState('#111111');
  const [fillColor, setFillColor] = useState('rgba(22, 152, 115, 0.14)');
  const [shapeFillEnabled, setShapeFillEnabled] = useState(false);
  const [lineWidth, setLineWidth] = useState(3);
  // Each element: stroke, shape, text, or image. Snapshots are intentionally plain JSON.
  const [elements, setElements] = useState([]);
  const [undoStack, setUndoStack] = useState([]);
  const [shapeStart, setShapeStart] = useState(null);
  const [shapePreview, setShapePreview] = useState(null);
  const [textDraft, setTextDraft] = useState(null);
  const [activeMenu, setActiveMenu] = useState(null);
  const [uploadedImages, setUploadedImages] = useState([]); // {src, x, y, w, h}

  const colors = [
    { value: '#111111', label: 'Ink' },
    { value: '#169873', label: 'Emerald' },
    { value: '#0ea5e9', label: 'Sky' },
    { value: '#ef4444', label: 'Red' },
    { value: '#f59e0b', label: 'Amber' },
    { value: '#8b5cf6', label: 'Violet' },
    { value: '#ec4899', label: 'Pink' },
  ];

  const highlighterColors = [
    { value: 'rgba(250, 204, 21, 0.4)', label: 'Yellow' },
    { value: 'rgba(74, 222, 128, 0.4)', label: 'Green' },
    { value: 'rgba(96, 165, 250, 0.4)', label: 'Blue' },
    { value: 'rgba(251, 146, 60, 0.4)', label: 'Orange' },
    { value: 'rgba(244, 114, 182, 0.4)', label: 'Pink' },
  ];

  const pencilTools = [
    { id: 'pencil', icon: 'Pencil', label: 'Pencil' },
    { id: 'colorPencil', icon: 'PenTool', label: 'Color pencil' },
    { id: 'marker', icon: 'Brush', label: 'Marker' },
  ];

  const highlighterTools = [
    { id: 'highlighter', icon: 'Highlighter', label: 'Soft highlighter' },
    { id: 'broadHighlighter', icon: 'Paintbrush', label: 'Wide highlighter' },
  ];

  const shapeTools = [
    { id: 'rect', icon: 'Square', label: 'Rectangle' },
    { id: 'circle', icon: 'Circle', label: 'Circle' },
    { id: 'triangle', icon: 'Triangle', label: 'Triangle' },
    { id: 'line', icon: 'Minus', label: 'Line' },
    { id: 'arrow', icon: 'ArrowUpRight', label: 'Arrow' },
  ];

  const fillColors = [
    { value: 'rgba(22, 152, 115, 0.14)', label: 'Emerald fill' },
    { value: 'rgba(14, 165, 233, 0.14)', label: 'Sky fill' },
    { value: 'rgba(250, 204, 21, 0.22)', label: 'Yellow fill' },
    { value: 'rgba(248, 113, 113, 0.16)', label: 'Red fill' },
    { value: 'rgba(139, 92, 246, 0.16)', label: 'Violet fill' },
    { value: 'rgba(255, 255, 255, 0.92)', label: 'White fill' },
  ];

  const shapeToolIds = shapeTools.map((item) => item.id);
  const drawToolIds = [...pencilTools, ...highlighterTools].map((item) => item.id);
  const isShapeTool = shapeToolIds.includes(tool);
  const isTextTool = tool === 'text';
  const isDrawTool = drawToolIds.includes(tool);
  const isHighlighterTool = highlighterTools.some((item) => item.id === tool);
  const isFillTool = tool === 'fill';

  const normalizeBox = (start, end, minWidth = 1, minHeight = 1) => {
    const rawX = Math.min(start.x, end.x);
    const rawY = Math.min(start.y, end.y);
    const width = Math.max(Math.abs(end.x - start.x), minWidth);
    const height = Math.max(Math.abs(end.y - start.y), minHeight);
    return {
      x: rawX,
      y: rawY,
      width,
      height,
      start: { x: rawX, y: rawY },
      end: { x: rawX + width, y: rawY + height },
    };
  };

  // --- Drawing helpers ---
  const drawStroke = (ctx, el) => {
    if (!el.points || el.points.length < 2) return;
    ctx.save();
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    if (el.tool === 'highlighter' || el.tool === 'broadHighlighter') {
      ctx.globalAlpha = 1;
      ctx.strokeStyle = el.color;
      ctx.lineWidth = el.width * (el.tool === 'broadHighlighter' ? 9 : 6);
      ctx.globalCompositeOperation = 'source-over';
    } else if (el.tool === 'colorPencil') {
      ctx.strokeStyle = el.color;
      ctx.lineWidth = el.width;
      ctx.globalAlpha = 0.65;
    } else if (el.tool === 'marker') {
      ctx.strokeStyle = el.color;
      ctx.lineWidth = el.width * 2.4;
      ctx.globalAlpha = 0.9;
    } else {
      ctx.strokeStyle = el.color;
      ctx.lineWidth = el.width;
    }

    ctx.beginPath();
    ctx.moveTo(el.points[0].x, el.points[0].y);
    for (let i = 1; i < el.points.length; i++) {
      ctx.lineTo(el.points[i].x, el.points[i].y);
    }
    ctx.stroke();

    // Color pencil texture: extra jittered thin parallel strokes
    if (el.tool === 'colorPencil' && el.points.length > 2) {
      ctx.globalAlpha = 0.25;
      ctx.lineWidth = Math.max(1, el.width * 0.4);
      for (let offset = -1; offset <= 1; offset += 2) {
        ctx.beginPath();
        ctx.moveTo(el.points[0].x + offset, el.points[0].y + offset);
        for (let i = 1; i < el.points.length; i++) {
          const jx = el.points[i].x + offset + (Math.random() - 0.5) * 1.2;
          const jy = el.points[i].y + offset + (Math.random() - 0.5) * 1.2;
          ctx.lineTo(jx, jy);
        }
        ctx.stroke();
      }
    }
    ctx.restore();
  };

  const drawArrowHead = (ctx, start, end, width) => {
    const angle = Math.atan2(end.y - start.y, end.x - start.x);
    const size = Math.max(12, width * 4);
    ctx.beginPath();
    ctx.moveTo(end.x, end.y);
    ctx.lineTo(end.x - size * Math.cos(angle - Math.PI / 6), end.y - size * Math.sin(angle - Math.PI / 6));
    ctx.moveTo(end.x, end.y);
    ctx.lineTo(end.x - size * Math.cos(angle + Math.PI / 6), end.y - size * Math.sin(angle + Math.PI / 6));
    ctx.stroke();
  };

  const drawShape = (ctx, el) => {
    ctx.save();
    ctx.strokeStyle = el.color;
    ctx.fillStyle = el.fill || 'transparent';
    ctx.lineWidth = el.width;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    const { start, end, shape } = el;
    const x = Math.min(start.x, end.x);
    const y = Math.min(start.y, end.y);
    const w = Math.abs(end.x - start.x);
    const h = Math.abs(end.y - start.y);

    if (shape === 'line' || shape === 'arrow') {
      ctx.beginPath();
      ctx.moveTo(start.x, start.y);
      ctx.lineTo(end.x, end.y);
      ctx.stroke();
      if (shape === 'arrow') drawArrowHead(ctx, start, end, el.width);
      ctx.restore();
      return;
    }

    ctx.beginPath();
    if (shape === 'rect') {
      ctx.rect(x, y, w, h);
    } else if (shape === 'circle') {
      const cx = (start.x + end.x) / 2;
      const cy = (start.y + end.y) / 2;
      const rx = w / 2;
      const ry = h / 2;
      ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
    } else if (shape === 'triangle') {
      const mx = (start.x + end.x) / 2;
      ctx.moveTo(mx, y);
      ctx.lineTo(x, y + h);
      ctx.lineTo(x + w, y + h);
      ctx.closePath();
    }
    if (el.fill) ctx.fill();
    ctx.stroke();
    ctx.restore();
  };

  const wrapTextLines = (ctx, text, maxWidth) => {
    const words = `${text || ''}`.split(/\s+/).filter(Boolean);
    if (words.length === 0) return [''];
    const lines = [];
    let line = '';
    words.forEach((word) => {
      const testLine = line ? `${line} ${word}` : word;
      if (ctx.measureText(testLine).width > maxWidth && line) {
        lines.push(line);
        line = word;
      } else {
        line = testLine;
      }
    });
    if (line) lines.push(line);
    return lines;
  };

  const drawTextBox = (ctx, el) => {
    const box = normalizeBox(el.start, el.end, 120, 48);
    const padding = 10;
    ctx.save();
    if (el.fill) {
      ctx.fillStyle = el.fill;
      ctx.fillRect(box.x, box.y, box.width, box.height);
    }
    ctx.strokeStyle = el.color;
    ctx.lineWidth = Math.max(1, el.width || 2);
    ctx.strokeRect(box.x, box.y, box.width, box.height);
    ctx.fillStyle = el.color;
    ctx.font = '14px Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
    ctx.textBaseline = 'top';
    const lines = wrapTextLines(ctx, el.text || 'Text', Math.max(32, box.width - padding * 2));
    const lineHeight = 19;
    lines.slice(0, Math.floor((box.height - padding * 2) / lineHeight) || 1).forEach((line, index) => {
      ctx.fillText(line, box.x + padding, box.y + padding + index * lineHeight);
    });
    ctx.restore();
  };

  const drawImage = (ctx, el, loadedImgs) => {
    const img = loadedImgs[el.src];
    if (img) {
      ctx.drawImage(img, el.x, el.y, el.w, el.h);
    }
  };

  // Preloaded images cache
  const imgCacheRef = useRef({});
  const loadImage = (src) => {
    if (imgCacheRef.current[src]) return Promise.resolve(imgCacheRef.current[src]);
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => { imgCacheRef.current[src] = img; resolve(img); };
      img.onerror = () => resolve(null);
      img.src = src;
    });
  };

  // Redraw all elements on the canvas
  const redrawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw background grid (never erased)
    ctx.save();
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.03)';
    ctx.lineWidth = 1;
    const gridSize = 25;
    const dpr = window.devicePixelRatio || 1;
    const logicalW = canvas.width / dpr;
    const logicalH = canvas.height / dpr;
    for (let x = 0; x < logicalW; x += gridSize) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, logicalH);
      ctx.stroke();
    }
    for (let y = 0; y < logicalH; y += gridSize) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(logicalW, y);
      ctx.stroke();
    }
    ctx.restore();

    // Draw all elements in order
    elements.forEach((el) => {
      if (el.type === 'stroke') drawStroke(ctx, el);
      else if (el.type === 'shape') drawShape(ctx, el);
      else if (el.type === 'text') drawTextBox(ctx, el);
      else if (el.type === 'image') drawImage(ctx, el, imgCacheRef.current);
    });

    // Draw live shape preview
    if (shapePreview?.type === 'text') drawTextBox(ctx, shapePreview);
    else if (shapePreview) drawShape(ctx, shapePreview);
  }, [elements, shapePreview]);

  // Handle incoming snapshot from sync
  useEffect(() => {
    if (snapshot) {
      try {
        const decoded = typeof snapshot === 'string' ? JSON.parse(snapshot) : snapshot;
        if (Array.isArray(decoded)) {
          // Load any images in the snapshot
          decoded.filter(e => e.type === 'image').forEach(e => loadImage(e.src));
          setElements(decoded);
        }
      } catch (e) {
        console.error('Failed to parse whiteboard snapshot:', e);
      }
    }
  }, [snapshot]);

  // Redraw whenever elements or preview changes
  useEffect(() => {
    redrawCanvas();
  }, [redrawCanvas]);

  // Handle canvas resize and scale for High-DPI screens
  useEffect(() => {
    const handleResize = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const rect = canvas.parentElement.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
      const ctx = canvas.getContext('2d');
      ctx.scale(dpr, dpr);
      redrawCanvas();
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, [redrawCanvas]);

  const getCanvasCoords = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  // --- Eraser: stroke hit-testing ---
  const distToSegment = (p, a, b) => {
    const dx = b.x - a.x, dy = b.y - a.y;
    const lenSq = dx * dx + dy * dy;
    if (lenSq === 0) return Math.hypot(p.x - a.x, p.y - a.y);
    let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / lenSq;
    t = Math.max(0, Math.min(1, t));
    return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
  };

  const pointInElement = (el, coords) => {
    if (el.type === 'image') {
      return coords.x >= el.x && coords.x <= el.x + el.w && coords.y >= el.y && coords.y <= el.y + el.h;
    }

    if (el.type === 'text') {
      const box = normalizeBox(el.start, el.end, 120, 48);
      return coords.x >= box.x && coords.x <= box.x + box.width && coords.y >= box.y && coords.y <= box.y + box.height;
    }

    if (el.type !== 'shape') return false;
    const { start, end, shape } = el;

    if (shape === 'line' || shape === 'arrow') {
      return distToSegment(coords, start, end) <= Math.max(8, (el.width || 2) * 2);
    }

    const box = normalizeBox(start, end);
    if (shape === 'circle') {
      const cx = box.x + box.width / 2;
      const cy = box.y + box.height / 2;
      const rx = Math.max(1, box.width / 2);
      const ry = Math.max(1, box.height / 2);
      return (((coords.x - cx) ** 2) / (rx ** 2)) + (((coords.y - cy) ** 2) / (ry ** 2)) <= 1;
    }

    return coords.x >= box.x && coords.x <= box.x + box.width && coords.y >= box.y && coords.y <= box.y + box.height;
  };

  const fillAt = (coords) => {
    setUndoStack([]);
    setElements((prev) => {
      const targetIndexFromEnd = [...prev].reverse().findIndex((el) => (
        (el.type === 'shape' && el.shape !== 'line' && el.shape !== 'arrow' && pointInElement(el, coords)) ||
        (el.type === 'text' && pointInElement(el, coords))
      ));
      if (targetIndexFromEnd < 0) return prev;

      const targetIndex = prev.length - 1 - targetIndexFromEnd;
      const next = prev.map((el, index) => (
        index === targetIndex ? { ...el, fill: shapeFillEnabled ? fillColor : null } : el
      ));
      onSnapshotChange(next);
      return next;
    });
  };

  const eraseAt = (coords) => {
    const eraserRadius = lineWidth * 3;
    setElements((prev) => {
      const filtered = prev.filter((el) => {
        if (el.type === 'stroke' && el.points) {
          for (let i = 0; i < el.points.length - 1; i++) {
            if (distToSegment(coords, el.points[i], el.points[i + 1]) < eraserRadius + (el.width || 2)) {
              return false;
            }
          }
        }
        if (el.type === 'shape' || el.type === 'text' || el.type === 'image') {
          if (pointInElement(el, coords)) return false;
        }
        return true;
      });
      return filtered;
    });
  };

  // --- Undo / Redo ---
  const handleUndo = () => {
    setElements((prev) => {
      if (prev.length === 0) return prev;
      const last = prev[prev.length - 1];
      setUndoStack((us) => [...us, last]);
      return prev.slice(0, -1);
    });
  };

  const handleRedo = () => {
    setUndoStack((prev) => {
      if (prev.length === 0) return prev;
      const last = prev[prev.length - 1];
      setElements((els) => [...els, last]);
      return prev.slice(0, -1);
    });
  };

  // --- Mouse event handlers ---
  const handleMouseDown = (e) => {
    if (e.button !== undefined && e.button !== 0) return;
    if (textDraft) return;
    if (activeMenu) setActiveMenu(null);
    const coords = getCanvasCoords(e);

    if (isFillTool) {
      fillAt(coords);
      return;
    }

    if (tool === 'eraser') {
      setIsDrawing(true);
      eraseAt(coords);
      return;
    }

    if (isShapeTool || isTextTool) {
      setShapeStart(coords);
      setIsDrawing(true);
      setShapePreview(isTextTool ? {
        type: 'text',
        start: coords,
        end: { x: coords.x + 180, y: coords.y + 64 },
        text: 'Text',
        color,
        fill: shapeFillEnabled ? fillColor : 'rgba(255, 255, 255, 0.92)',
        width: lineWidth,
      } : null);
      return;
    }

    // Drawing tools
    setIsDrawing(true);
    setUndoStack([]);
    const newStroke = {
      type: 'stroke',
      points: [coords],
      tool,
      color: isHighlighterTool ? (highlighterColors.find(c => c.value === color) ? color : highlighterColors[0].value) : color,
      width: lineWidth,
    };
    setElements((prev) => [...prev, newStroke]);
  };

  const handleMouseMove = (e) => {
    onPointerMove(e);
    if (!isDrawing) return;
    const coords = getCanvasCoords(e);

    if (tool === 'eraser') {
      eraseAt(coords);
      return;
    }

    if ((isShapeTool || isTextTool) && shapeStart) {
      setShapePreview(isTextTool ? {
        type: 'text',
        start: shapeStart,
        end: coords,
        text: 'Text',
        color,
        fill: shapeFillEnabled ? fillColor : 'rgba(255, 255, 255, 0.92)',
        width: lineWidth,
      } : {
        type: 'shape',
        shape: tool,
        start: shapeStart,
        end: coords,
        color,
        fill: shapeFillEnabled && tool !== 'line' && tool !== 'arrow' ? fillColor : null,
        width: lineWidth,
      });
      return;
    }

    // Drawing tools
    setElements((prev) => {
      if (prev.length === 0) return prev;
      const lastEl = { ...prev[prev.length - 1] };
      if (lastEl.type !== 'stroke') return prev;
      lastEl.points = [...lastEl.points, coords];
      return [...prev.slice(0, -1), lastEl];
    });
  };

  const handleMouseUp = () => {
    if (!isDrawing) return;
    setIsDrawing(false);

    if (isTextTool && shapeStart) {
      const box = normalizeBox(shapeStart, shapePreview?.end || { x: shapeStart.x + 180, y: shapeStart.y + 64 }, 180, 64);
      setTextDraft({
        start: box.start,
        end: box.end,
        text: '',
        color,
        fill: shapeFillEnabled ? fillColor : 'rgba(255, 255, 255, 0.92)',
        width: lineWidth,
      });
      setShapeStart(null);
      setShapePreview(null);
      return;
    }

    if (isShapeTool && shapeStart && shapePreview) {
      const { start, end } = shapePreview;
      const dist = Math.hypot(end.x - start.x, end.y - start.y);
      if (dist > 5) {
        setElements((prev) => {
          const next = [...prev, { ...shapePreview }];
          onSnapshotChange(next);
          return next;
        });
      }
      setShapeStart(null);
      setShapePreview(null);
      return;
    }

    // Sync
    setElements((cur) => { onSnapshotChange(cur); return cur; });
  };

  const handleClear = () => {
    setElements([]);
    setUndoStack([]);
    setTextDraft(null);
    onSnapshotChange([]);
  };

  const commitTextDraft = () => {
    if (!textDraft) return;
    const text = textDraft.text.trim();
    if (!text) {
      setTextDraft(null);
      return;
    }

    setUndoStack([]);
    setElements((prev) => {
      const next = [...prev, { type: 'text', ...textDraft, text }];
      onSnapshotChange(next);
      return next;
    });
    setTextDraft(null);
  };

  // --- Image upload ---
  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const src = ev.target.result;
      const img = new Image();
      img.onload = () => {
        imgCacheRef.current[src] = img;
        // Scale image to fit within canvas nicely
        const maxW = 300, maxH = 250;
        let w = img.naturalWidth, h = img.naturalHeight;
        if (w > maxW) { h = h * (maxW / w); w = maxW; }
        if (h > maxH) { w = w * (maxH / h); h = maxH; }
        const newEl = { type: 'image', src, x: 40, y: 80, w, h };
        setElements((prev) => [...prev, newEl]);
        onSnapshotChange([...elements, newEl]);
      };
      img.src = src;
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  // Keyboard shortcut for undo/redo
  useEffect(() => {
    const handleKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        e.preventDefault();
        if (e.shiftKey) handleRedo();
        else handleUndo();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);

  const isHighlighterToolId = (id) => highlighterTools.some((item) => item.id === id);
  const activeColors = isHighlighterTool ? highlighterColors : colors;
  const showStrokeColors = isDrawTool || isShapeTool || isTextTool;
  const textDraftBox = textDraft ? normalizeBox(textDraft.start, textDraft.end, 180, 64) : null;

  const selectTool = (id) => {
    setTool(id);
    setActiveMenu(null);
    if (isHighlighterToolId(id) && !highlighterColors.find(c => c.value === color)) {
      setColor(highlighterColors[0].value);
    } else if (!isHighlighterToolId(id) && highlighterColors.find(c => c.value === color)) {
      setColor(colors[0].value);
    }
    if (id === 'fill') setShapeFillEnabled(true);
  };

  const ToolMenu = ({ menuId, icon, label, options }) => {
    const activeOption = options.find((item) => item.id === tool);
    const isActive = Boolean(activeOption);

    return (
      <div className="whiteboard-tool-menu">
        <button
          type="button"
          onClick={() => setActiveMenu((current) => current === menuId ? null : menuId)}
          className={cn(
            'whiteboard-tool-button',
            isActive && 'whiteboard-tool-button-active'
          )}
          title={activeOption?.label || label}
          aria-label={activeOption?.label || label}
        >
          <Icon name={activeOption?.icon || icon} size={15} />
          <Icon name="ChevronRight" size={10} className="whiteboard-tool-caret" />
        </button>
        {activeMenu === menuId && (
          <div className="whiteboard-tool-popover whiteboard-tool-popover-grid">
            {options.map((option) => (
              <button
                key={option.id}
                type="button"
                onClick={() => selectTool(option.id)}
                className={cn(
                  'whiteboard-popover-button',
                  tool === option.id && 'whiteboard-popover-button-active'
                )}
                title={option.label}
                aria-label={option.label}
              >
                <Icon name={option.icon} size={15} />
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  const ColorSwatch = ({ item, value, onSelect }) => (
    <button
      key={item.value}
      type="button"
      onClick={() => onSelect(item.value)}
      style={{ backgroundColor: item.value }}
      className={cn(
        'whiteboard-color-swatch',
        value === item.value && 'whiteboard-color-swatch-active'
      )}
      title={item.label}
      aria-label={item.label}
    />
  );

  const canvasCursor = tool === 'eraser' || isFillTool
    ? 'cursor-cell'
    : isTextTool
      ? 'cursor-text'
      : 'cursor-crosshair';

  return (
    <div className="relative flex h-full w-full flex-col overflow-hidden bg-white" onMouseMove={onPointerMove}>
      {/* Floating Toolbar */}
      <div className="whiteboard-tool-rail">
        <ToolMenu menuId="pencil" icon="Pencil" label="Pencil" options={pencilTools} />
        <ToolMenu menuId="highlighter" icon="Highlighter" label="Highlighter" options={highlighterTools} />
        <ToolMenu menuId="shapes" icon="Shapes" label="Shapes" options={shapeTools} />

        <div className="whiteboard-rail-divider" />

        <button
          type="button"
          onClick={() => selectTool('text')}
          className={cn(
            'whiteboard-tool-button',
            isTextTool && 'whiteboard-tool-button-active'
          )}
          title="Text box"
          aria-label="Text box"
        >
          <Icon name="Type" size={15} />
        </button>

        <button
          type="button"
          onClick={() => selectTool('eraser')}
          className={cn(
            'whiteboard-tool-button',
            tool === 'eraser' && 'whiteboard-tool-button-active'
          )}
          title="Eraser"
          aria-label="Eraser"
        >
          <Icon name="Eraser" size={15} />
        </button>

        <div className="whiteboard-rail-divider" />

        {showStrokeColors && (
          <div className="whiteboard-color-grid">
            {activeColors.map((c) => (
              <ColorSwatch key={c.value} item={c} value={color} onSelect={setColor} />
            ))}
          </div>
        )}

        <div className="whiteboard-tool-menu">
          <button
            type="button"
            onClick={() => setActiveMenu((current) => current === 'fill' ? null : 'fill')}
            className={cn(
              'whiteboard-tool-button',
              isFillTool && 'whiteboard-tool-button-active'
            )}
            title="Fill"
            aria-label="Fill"
          >
            <Icon name="PaintBucket" size={15} />
            <Icon name="ChevronRight" size={10} className="whiteboard-tool-caret" />
          </button>
          {activeMenu === 'fill' && (
            <div className="whiteboard-tool-popover whiteboard-fill-popover">
              <div className="flex items-center justify-center">
                <button
                  type="button"
                  onClick={() => setShapeFillEnabled((value) => !value)}
                  className={cn(
                    'relative h-6 w-10 rounded-full transition-colors',
                    shapeFillEnabled ? 'bg-[var(--lp-accent)]' : 'bg-slate-200'
                  )}
                  title={shapeFillEnabled ? 'Disable fill' : 'Enable fill'}
                  aria-label={shapeFillEnabled ? 'Disable fill' : 'Enable fill'}
                >
                  <span className={cn(
                    'absolute top-1 h-4 w-4 rounded-full bg-white transition-transform',
                    shapeFillEnabled ? 'translate-x-5' : 'translate-x-1'
                  )} />
                </button>
              </div>
              <div className="mt-3 grid grid-cols-3 gap-2">
                {fillColors.map((item) => (
                  <ColorSwatch key={item.value} item={item} value={fillColor} onSelect={setFillColor} />
                ))}
              </div>
              <button
                type="button"
                onClick={() => selectTool('fill')}
                className={cn(
                  'whiteboard-popover-button mt-3',
                  isFillTool && 'whiteboard-popover-button-active'
                )}
                title="Fill existing"
                aria-label="Fill existing"
              >
                <Icon name="PaintBucket" size={15} />
              </button>
            </div>
          )}
        </div>

        <div className="whiteboard-size-control" title="Stroke size">
          <input
            type="range"
            min={1}
            max={isHighlighterTool ? 12 : 20}
            value={lineWidth}
            onChange={(e) => setLineWidth(Number(e.target.value))}
            className="whiteboard-size-slider"
            style={{ accentColor: 'var(--lp-accent)' }}
            aria-label="Stroke size"
          />
          <span className="whiteboard-size-value">{lineWidth}</span>
        </div>

        <div className="whiteboard-rail-divider" />

        <div className="whiteboard-rail-actions">
          <button type="button" onClick={handleUndo} className="whiteboard-rail-icon-button" title="Undo (Ctrl+Z)" aria-label="Undo">
            <Icon name="Undo2" size={14} />
          </button>
          <button type="button" onClick={handleRedo} className="whiteboard-rail-icon-button" title="Redo (Ctrl+Shift+Z)" aria-label="Redo">
            <Icon name="Redo2" size={14} />
          </button>

          <div className="whiteboard-rail-divider" />

          {/* Image upload */}
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="whiteboard-rail-icon-button"
            title="Upload image"
            aria-label="Upload image"
          >
            <Icon name="ImagePlus" size={14} />
          </button>
          <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />

          <div className="whiteboard-rail-divider" />

          <button
            type="button"
            onClick={handleClear}
            className="whiteboard-rail-icon-button whiteboard-rail-icon-button-danger"
            title="Clear board"
            aria-label="Clear board"
          >
            <Icon name="Trash2" size={13} />
          </button>
        </div>
      </div>

      {/* Canvas Area */}
      <div className={cn('flex-1 w-full relative bg-white min-h-0', canvasCursor)}>
        <canvas
          ref={canvasRef}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onTouchStart={handleMouseDown}
          onTouchMove={handleMouseMove}
          onTouchEnd={handleMouseUp}
          className="absolute inset-0 block w-full h-full touch-none"
        />
        {textDraft && textDraftBox && (
          <div
            className="absolute z-30 rounded-xl border border-[var(--lp-accent)] bg-white shadow-xl"
            style={{
              left: `${textDraftBox.x}px`,
              top: `${textDraftBox.y}px`,
              width: `${textDraftBox.width}px`,
              minHeight: `${textDraftBox.height}px`,
            }}
          >
            <textarea
              autoFocus
              value={textDraft.text}
              onChange={(event) => setTextDraft((current) => current ? { ...current, text: event.target.value } : current)}
              onKeyDown={(event) => {
                if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
                  event.preventDefault();
                  commitTextDraft();
                }
                if (event.key === 'Escape') setTextDraft(null);
              }}
              placeholder="Text"
              className="block min-h-[64px] w-full resize-none rounded-t-xl border-0 bg-transparent p-3 text-sm text-[var(--lp-ink)] outline-none"
            />
            <div className="flex justify-end gap-1 border-t border-[var(--lp-line)] bg-white/90 px-2 py-1.5">
              <button
                type="button"
                onClick={() => setTextDraft(null)}
                className="flex h-7 w-7 items-center justify-center rounded-lg text-[var(--lp-ink-soft)] transition-colors hover:bg-slate-100"
                title="Cancel text"
              >
                <Icon name="X" size={14} />
              </button>
              <button
                type="button"
                onClick={commitTextDraft}
                className="flex h-7 w-7 items-center justify-center rounded-lg bg-[var(--lp-accent)] text-white transition-colors hover:bg-[var(--lp-accent)]/90"
                title="Save text"
              >
                <Icon name="Check" size={14} />
              </button>
            </div>
          </div>
        )}
        <PointerLayer participants={participants} currentPointer={pointer} />
      </div>
    </div>
  );
};

const ChatPanel = ({ messages, draft, setDraft, onSend, onClose }) => (
  <aside className="flex min-h-0 w-full flex-col border-l border-[var(--lp-line)] bg-white lg:w-[330px]">
    <div className="flex h-14 items-center justify-between border-b border-[var(--lp-line)] px-4">
      <div>
        <p className="text-sm text-[var(--lp-ink)]">Room chat</p>
        <p className="text-xs text-[var(--lp-ink-soft)]">Shared with every participant</p>
      </div>
      <button
        type="button"
        onClick={onClose}
        className="flex h-8 w-8 items-center justify-center rounded-full border border-[var(--lp-line)] bg-white text-[var(--lp-ink-soft)] hover:bg-[var(--lp-surface)]"
        title="Close chat"
      >
        <Icon name="X" size={15} />
      </button>
    </div>
    <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4">
      {messages.map((message) => (
        <div key={message.id} className="rounded-xl border border-[var(--lp-line)] bg-[var(--lp-surface)] p-3">
          <div className="flex items-center justify-between gap-3">
            <span className="text-sm text-[var(--lp-ink)]">{message.author}</span>
            <span className="font-mono text-[10px] text-[var(--lp-ink-soft)]">{message.time}</span>
          </div>
          <p className="mt-1 text-sm leading-relaxed text-[var(--lp-ink-soft)]">{message.body}</p>
        </div>
      ))}
    </div>
    <div className="border-t border-[var(--lp-line)] p-3">
      <div className="flex gap-2">
        <input
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') onSend();
          }}
          placeholder="Message the room"
          className="h-10 min-w-0 flex-1 rounded-full border border-[var(--lp-line)] bg-white px-4 text-sm text-[var(--lp-ink)] outline-none focus:border-[var(--lp-accent)]"
        />
        <button
          type="button"
          onClick={onSend}
          className="flex h-10 w-10 items-center justify-center rounded-full bg-[var(--lp-accent)] text-white hover:bg-[var(--lp-accent)]/90"
          title="Send message"
        >
          <Icon name="Send" size={16} />
        </button>
      </div>
    </div>
  </aside>
);

const AIAssistantPanel = ({
  messages,
  draft,
  setDraft,
  onSend,
  onClose,
  isThinking,
  canSend,
}) => (
  <aside className="flex min-h-0 w-full flex-col border-l border-[var(--lp-line)] bg-white lg:w-[380px]">
    <div className="flex h-14 items-center justify-between border-b border-[var(--lp-line)] px-4">
      <div className="flex items-center gap-2">
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[var(--lp-accent)] text-white">
          <Icon name="Sparkles" size={15} />
        </span>
        <div>
          <p className="text-sm text-[var(--lp-ink)]">AI help</p>
          <p className="text-xs text-[var(--lp-ink-soft)]">Visible to interviewer and candidate</p>
        </div>
      </div>
      <button
        type="button"
        onClick={onClose}
        className="flex h-8 w-8 items-center justify-center rounded-full border border-[var(--lp-line)] bg-white text-[var(--lp-ink-soft)] hover:bg-[var(--lp-surface)]"
        title="Close AI help"
      >
        <Icon name="X" size={15} />
      </button>
    </div>

    <div className="min-h-0 flex-1 space-y-3 overflow-y-auto bg-[var(--lp-surface)] p-4">
      {messages.length === 0 ? (
        <div className="flex h-full flex-col items-center justify-center rounded-2xl border border-dashed border-[var(--lp-line)] bg-white p-6 text-center">
          <Icon name="MessageCircleQuestion" size={28} className="text-[var(--lp-ink-soft)]" />
          <p className="mt-3 text-sm text-[var(--lp-ink)]">No AI messages yet.</p>
          <p className="mt-1 text-xs leading-relaxed text-[var(--lp-ink-soft)]">
            Candidate can ask for hints, debugging help, or explanation. Interviewer can observe the full exchange.
          </p>
        </div>
      ) : (
        messages.map((message) => (
          <div
            key={message.id}
            className={cn(
              'max-w-[92%] rounded-2xl border p-3 shadow-sm',
              message.role === 'user'
                ? 'ml-auto border-[var(--lp-accent)]/20 bg-[var(--lp-accent)]/10'
                : 'mr-auto border-[var(--lp-line)] bg-white'
            )}
          >
            <div className="flex items-center justify-between gap-3">
              <span className="text-xs text-[var(--lp-ink)]">{message.author}</span>
              <span className="font-mono text-[10px] text-[var(--lp-ink-soft)]">{message.time}</span>
            </div>
            <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-[var(--lp-ink-soft)]">{message.text}</p>
          </div>
        ))
      )}
      {isThinking && (
        <div className="mr-auto flex max-w-[92%] items-center gap-2 rounded-2xl border border-[var(--lp-line)] bg-white p-3 text-sm text-[var(--lp-ink-soft)] shadow-sm">
          <span className="h-3 w-3 animate-spin rounded-full border-2 border-[var(--lp-accent)] border-t-transparent" />
          AI is thinking...
        </div>
      )}
    </div>

    <div className="border-t border-[var(--lp-line)] p-3">
      {!canSend && (
        <p className="mb-2 rounded-xl bg-[var(--lp-surface)] px-3 py-2 text-xs leading-relaxed text-[var(--lp-ink-soft)]">
          Interviewer view is read-only. Candidate AI usage appears here.
        </p>
      )}
      <div className="flex gap-2">
        <input
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') onSend();
          }}
          disabled={!canSend || isThinking}
          placeholder={canSend ? 'Ask AI for help' : 'Waiting for candidate'}
          className="h-10 min-w-0 flex-1 rounded-full border border-[var(--lp-line)] bg-white px-4 text-sm text-[var(--lp-ink)] outline-none focus:border-[var(--lp-accent)] disabled:bg-[var(--lp-surface)] disabled:text-[var(--lp-ink-soft)]"
        />
        <button
          type="button"
          onClick={onSend}
          disabled={!canSend || isThinking || !draft.trim()}
          className="flex h-10 w-10 items-center justify-center rounded-full bg-[var(--lp-accent)] text-white hover:bg-[var(--lp-accent)]/90 disabled:cursor-not-allowed disabled:opacity-50"
          title="Send AI message"
        >
          <Icon name="Send" size={16} />
        </button>
      </div>
    </div>
  </aside>
);

const ParticipantsPanel = ({ participants, onClose }) => (
  <aside className="flex min-h-0 w-full flex-col border-l border-[var(--lp-line)] bg-white lg:w-[300px]">
    <div className="flex h-14 items-center justify-between border-b border-[var(--lp-line)] px-4">
      <div>
        <p className="text-sm text-[var(--lp-ink)]">Participants</p>
        <p className="text-xs text-[var(--lp-ink-soft)]">{participants.length} people in room</p>
      </div>
      <button
        type="button"
        onClick={onClose}
        className="flex h-8 w-8 items-center justify-center rounded-full border border-[var(--lp-line)] bg-white text-[var(--lp-ink-soft)] hover:bg-[var(--lp-surface)]"
        title="Close participants"
      >
        <Icon name="X" size={15} />
      </button>
    </div>
    <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4">
      {participants.map((participant) => (
        <div key={participant.id} className="flex items-center justify-between rounded-xl border border-[var(--lp-line)] bg-white p-3">
          <div className="flex min-w-0 items-center gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm text-white" style={{ backgroundColor: participant.color }}>
              {getInitials(participant.name)}
            </span>
            <div className="min-w-0">
              <p className="truncate text-sm text-[var(--lp-ink)]">{participant.name}</p>
              <p className="text-xs capitalize text-[var(--lp-ink-soft)]">{participant.role}</p>
            </div>
          </div>
          <span className="rounded-full bg-[var(--lp-surface)] px-2 py-1 text-[10px] text-[var(--lp-ink-soft)]">
            {participant.isCurrent ? 'You' : 'Live'}
          </span>
        </div>
      ))}
    </div>
  </aside>
);

const feedbackCriteria = [
  { id: 'overall', label: 'Overall interview experience' },
  { id: 'audioVideo', label: 'Audio and video quality' },
  { id: 'collaboration', label: 'IDE and whiteboard collaboration' },
  { id: 'flow', label: 'Interview flow and controls' },
  { id: 'confidence', label: 'Confidence using this for real interviews' },
];

const BusinessInterviewFeedbackScreen = ({ room }) => {
  const navigate = useNavigate();
  const [ratings, setRatings] = useState(() => (
    feedbackCriteria.reduce((acc, item) => ({ ...acc, [item.id]: 0 }), {})
  ));
  const [comment, setComment] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const allRated = feedbackCriteria.every((item) => ratings[item.id] > 0);

  const handleSubmit = () => {
    if (!allRated) return;

    try {
      const existing = JSON.parse(localStorage.getItem('prepxai_business_interview_ratings') || '[]');
      localStorage.setItem('prepxai_business_interview_ratings', JSON.stringify([
        ...existing,
        {
          roomId: room.id,
          createdAt: new Date().toISOString(),
          ratings,
          comment,
        },
      ]));
    } catch {
      // Ignore local feedback persistence failures.
    }

    setSubmitted(true);
  };

  return (
    <div className="landing-page min-h-screen bg-white px-5 py-8 text-[var(--lp-ink)]">
      <div className="mx-auto flex min-h-[calc(100vh-4rem)] max-w-3xl items-center">
        <section className="w-full rounded-2xl border border-[var(--lp-line)] bg-white p-5 sm:p-7">
          <div className="flex items-start gap-3">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[var(--lp-accent)]/10 text-[var(--lp-accent)]">
              <Icon name={submitted ? 'CheckCircle2' : 'Star'} size={20} />
            </span>
            <div>
              <p className="text-sm font-medium text-[var(--lp-accent)]">{room.title}</p>
              <h1 className="mt-1 text-2xl font-semibold text-[var(--lp-ink)]">
                {submitted ? 'Thanks for the feedback' : 'Rate your interview experience'}
              </h1>
              <p className="mt-2 text-sm leading-relaxed text-[var(--lp-ink-soft)]">
                {submitted
                  ? 'Your feedback has been saved for this interview room.'
                  : 'Rate each area from 1 to 5 stars so we can improve the business interview room.'}
              </p>
            </div>
          </div>

          {!submitted ? (
            <>
              <div className="mt-7 space-y-4">
                {feedbackCriteria.map((item) => (
                  <div key={item.id} className="flex flex-col gap-3 rounded-xl border border-[var(--lp-line)] bg-transparent p-4 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm font-medium text-[var(--lp-ink)]">{item.label}</p>
                      <p className="mt-1 text-xs text-[var(--lp-ink-soft)]">1 star is poor, 5 stars is excellent.</p>
                    </div>
                    <div className="flex shrink-0 gap-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          type="button"
                          onClick={() => setRatings((current) => ({ ...current, [item.id]: star }))}
                          className="flex h-10 w-10 items-center justify-center rounded-full transition hover:bg-[var(--lp-surface)]"
                          title={`${item.label}: ${star} star${star > 1 ? 's' : ''}`}
                        >
                          <Icon
                            name="Star"
                            size={23}
                            className={star <= ratings[item.id] ? 'fill-yellow-400 text-yellow-400' : 'text-[var(--lp-ink-soft)]/35'}
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <textarea
                value={comment}
                onChange={(event) => setComment(event.target.value)}
                placeholder="Optional notes"
                className="mt-5 h-28 w-full resize-none rounded-xl border border-[var(--lp-line)] bg-transparent p-3 text-sm text-[var(--lp-ink)] outline-none focus:border-[var(--lp-accent)]"
              />

              <div className="mt-5 flex flex-wrap justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => navigate('/for-businesses/interviews')}
                  className="rounded-full border-[var(--lp-line)] bg-white px-5 text-sm text-[var(--lp-ink)] hover:bg-[var(--lp-surface)]"
                >
                  Skip
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={!allRated}
                  className="rounded-full bg-[var(--lp-accent)] px-5 text-sm text-white hover:bg-[var(--lp-accent)]/90 disabled:opacity-50"
                >
                  Submit feedback
                </Button>
              </div>
            </>
          ) : (
            <div className="mt-7 flex flex-wrap justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => navigate(`/for-businesses/interviews/${room.id}`)}
                className="rounded-full border-[var(--lp-line)] bg-white px-5 text-sm text-[var(--lp-ink)] hover:bg-[var(--lp-surface)]"
              >
                Back to room
              </Button>
              <Button
                onClick={() => navigate('/for-businesses/interviews')}
                className="rounded-full bg-[var(--lp-accent)] px-5 text-sm text-white hover:bg-[var(--lp-accent)]/90"
              >
                Create another room
              </Button>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

const InterviewRoom = ({ room, profile, onLeave, onRoomChange }) => {
  const [mode, setModeState] = useState('meet');
  const [micOn, setMicOn] = useState(profile.micOn);
  const [cameraOn, setCameraOn] = useState(profile.cameraOn);
  const [chatOpen, setChatOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [participantsOpen, setParticipantsOpen] = useState(false);
  const [proctorOpen, setProctorOpen] = useState(false);
  const [selectedQuestionId, setSelectedQuestionId] = useState(room.selectedQuestionId || '');
  const [language, setLanguage] = useState('javascript');
  const [code, setCode] = useState(starterCode.javascript);
  const [customInput, setCustomInput] = useState('');
  const [runResult, setRunResult] = useState(null);
  const [isRunningCode, setIsRunningCode] = useState(false);
  const [codeSelection, setCodeSelection] = useState(null);
  const [whiteboardSnapshot, setWhiteboardSnapshot] = useState(null);
  const [aiEnabled, setAiEnabled] = useState(Boolean(room.aiEnabled));
  const [aiMessages, setAiMessages] = useState([]);
  const [aiDraft, setAiDraft] = useState('');
  const [isAiThinking, setIsAiThinking] = useState(false);
  const [draft, setDraft] = useState('');
  const [pointer, setPointer] = useState(null);
  const [remoteStreams, setRemoteStreams] = useState({});
  const [participantMap, setParticipantMap] = useState(() => ({
    [profile.participantId]: {
      id: profile.participantId,
      name: profile.name,
      role: profile.role,
      micOn: profile.micOn,
      cameraOn: profile.cameraOn,
      status: profile.role === 'interviewer' ? 'Leading the round' : 'Ready to solve',
      active: true,
    },
  }));
  const peerConnectionsRef = useRef(new Map());
  const offeredPeersRef = useRef(new Set());
  const sendRef = useRef(() => {});
  const localStreamRef = useRef(null);
  const rtcSignalHandlerRef = useRef(null);
  const proctorVideoRef = useRef(null);
  const ingestProctorEventRef = useRef(() => {});
  const ingestProctorEventsRef = useRef(() => {});
  const ingestProctorSnapshotRef = useRef(() => {});
  const toastedProctorEventsRef = useRef(new Set());
  const [editorTheme, setEditorTheme] = useState(() =>
    typeof document !== 'undefined' && document.documentElement.classList.contains('dark') ? 'vs-dark' : 'light'
  );

  const { stream: localStream, error: mediaError } = useLocalMedia({ micOn, cameraOn });
  const selectedQuestion = questionBank.find((question) => question.id === selectedQuestionId) || null;
  const canSelectQuestion = profile.role === 'interviewer';

  const participants = useMemo(() => {
    return Object.values(participantMap)
      .sort((a, b) => {
        if (a.id === profile.participantId) return -1;
        if (b.id === profile.participantId) return 1;
        if (a.role === 'interviewer' && b.role !== 'interviewer') return -1;
        if (b.role === 'interviewer' && a.role !== 'interviewer') return 1;
        return (a.name || '').localeCompare(b.name || '');
      })
      .map((participant, index) => ({
        micOn: true,
        cameraOn: true,
        status: participant.role === 'interviewer' ? 'Leading the round' : 'Ready in room',
        ...participant,
        color: participant.color || participantPalette[index % participantPalette.length],
        isCurrent: participant.id === profile.participantId,
      }));
  }, [participantMap, profile.participantId]);

  const [messages, setMessages] = useState(() => [
    { id: 'm1', author: 'System', body: 'Room created. Start in Meet mode, then open IDE or whiteboard when ready.', time: 'now' },
  ]);

  // NOTE: whiteboardSnapshot is intentionally excluded from roomState.
  // Including it causes every brush stroke to re-broadcast the full state
  // (including selectedQuestionId), which overwrites the question on remote
  // participants and makes it appear to disappear. Whiteboard sync is handled
  // separately via handleWhiteboardSnapshot → publishState({ whiteboardSnapshot }).
  const roomState = useMemo(() => ({
    mode,
    selectedQuestionId,
    language,
    code,
    customInput,
    runResult,
    codeSelection,
    aiEnabled,
    aiMessages,
  }), [aiEnabled, aiMessages, code, codeSelection, customInput, language, mode, runResult, selectedQuestionId]);

  const applyIncomingState = useCallback((state = {}) => {
    if (state.mode) setModeState(state.mode);
    if (typeof state.selectedQuestionId === 'string') setSelectedQuestionId(state.selectedQuestionId);
    if (state.language) setLanguage(state.language);
    if (typeof state.code === 'string') setCode(state.code);
    if (typeof state.customInput === 'string') setCustomInput(state.customInput);
    if (state.runResult !== undefined) setRunResult(state.runResult);
    if (state.codeSelection !== undefined) setCodeSelection(state.codeSelection);
    if (state.whiteboardSnapshot !== undefined) setWhiteboardSnapshot(state.whiteboardSnapshot);
    if (typeof state.aiEnabled === 'boolean') setAiEnabled(state.aiEnabled);
    if (Array.isArray(state.aiMessages)) setAiMessages(state.aiMessages);
  }, []);

  const addRemoteMessage = useCallback((message) => {
    const payload = message.payload || {};
    if (!payload.body) return;
    setMessages((items) => {
      const id = payload.id || `${message.sender_id}-${message.sent_at}`;
      if (items.some((item) => item.id === id)) return items;
      return [
        ...items,
        {
          id,
          author: payload.author || payload.name || 'Participant',
          body: payload.body,
          time: payload.time || new Date(message.sent_at || Date.now()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        },
      ];
    });
  }, []);

  const upsertParticipant = useCallback((payload = {}, senderId = '') => {
    const id = payload.participant_id || payload.id || senderId;
    if (!id) return;

    setParticipantMap((current) => ({
      ...current,
      [id]: {
        ...(current[id] || {}),
        id,
        name: payload.name || current[id]?.name || (id === profile.participantId ? profile.name : 'Participant'),
        role: payload.role || current[id]?.role || 'candidate',
        micOn: payload.micOn ?? current[id]?.micOn ?? true,
        cameraOn: payload.cameraOn ?? current[id]?.cameraOn ?? true,
        pointer: payload.pointer || current[id]?.pointer,
        status: payload.status || current[id]?.status || 'Live in room',
        active: payload.active ?? current[id]?.active ?? false,
      },
    }));
  }, [profile.name, profile.participantId]);

  const openProctorLogs = useCallback(() => {
    setParticipantsOpen(false);
    setAiOpen(false);
    setChatOpen(false);
    setProctorOpen(true);
  }, []);

  const showProctorToast = useCallback((event = {}) => {
    if (!event.signal) return;

    const id = event.id || `${event.signal}-${event.timestamp || event.event_count || JSON.stringify(event.details || {}).slice(0, 80)}`;
    if (toastedProctorEventsRef.current.has(id)) return;
    if (toastedProctorEventsRef.current.size > 200) {
      toastedProctorEventsRef.current.clear();
    }
    toastedProctorEventsRef.current.add(id);

    const severity = event.severity || 'low';
    const confidence = Math.round((event.confidence || 0) * 100);
    const description = `${severity.toUpperCase()} alert • ${confidence}% confidence`;
    const toastId = `proctor-${id}`;
    const options = {
      id: toastId,
      description,
      duration: severity === 'high' || severity === 'critical' ? 7000 : 5000,
      action: {
        label: 'Open logs',
        onClick: openProctorLogs,
      },
      cancel: {
        label: '✕',
        onClick: () => toast.dismiss(toastId),
      },
    };
    const message = event.message || 'Interview integrity alert';

    if (severity === 'high' || severity === 'critical') {
      toast.error(message, options);
      return;
    }
    toast(message, options);
  }, [openProctorLogs]);


  const handleIncomingMessage = useCallback((message) => {
    if (!message?.type) return;
    const senderId = message.sender_id;
    const payload = message.payload || {};

    if (message.type === 'room_snapshot') {
      applyIncomingState(payload.state || {});
      if (payload.participants) {
        const entries = Array.isArray(payload.participants)
          ? payload.participants
          : Object.values(payload.participants);
        entries.forEach((participant) => upsertParticipant({
          participant_id: participant.id,
          name: participant.name,
          role: participant.role,
        }, participant.id));
      }
      if (Array.isArray(payload.chat)) {
        payload.chat.forEach(addRemoteMessage);
      }
      if (profile.role === 'interviewer' && Array.isArray(payload.proctor_events)) {
        ingestProctorEventsRef.current(
          payload.proctor_events
            .map((item) => item.payload)
            .filter(Boolean)
        );
      }
      return;
    }

    if (message.type === 'proctor_event' && profile.role === 'interviewer') {
      ingestProctorEventRef.current(payload);
      showProctorToast(payload);
      return;
    }

    if (message.type === 'proctor_snapshot' && profile.role === 'interviewer') {
      ingestProctorSnapshotRef.current(payload);
      return;
    }

    if (senderId === profile.participantId) return;

    if (message.type === 'state_update') {
      applyIncomingState(payload);
      return;
    }

    if (message.type === 'chat_message') {
      addRemoteMessage(message);
      return;
    }

    if (message.type === 'participant_joined' || message.type === 'participant_update') {
      upsertParticipant(payload, senderId);
      return;
    }

    if (message.type === 'participant_left') {
      const leavingId = payload.participant_id || senderId;
      setParticipantMap((current) => {
        const next = { ...current };
        delete next[leavingId];
        return next;
      });
      setRemoteStreams((current) => {
        const next = { ...current };
        delete next[leavingId];
        return next;
      });
      const pc = peerConnectionsRef.current.get(leavingId);
      pc?.close();
      peerConnectionsRef.current.delete(leavingId);
      offeredPeersRef.current.delete(leavingId);
      return;
    }

    if (message.type === 'pointer_update') {
      upsertParticipant(payload, senderId);
      return;
    }

    if (message.type === 'rtc_signal') {
      rtcSignalHandlerRef.current?.(payload);
    }
  }, [addRemoteMessage, applyIncomingState, profile.participantId, profile.role, showProctorToast, upsertParticipant]);

  const { send, status: syncStatus } = useBusinessRoomSync({
    roomId: room.id,
    profile,
    onMessage: handleIncomingMessage,
  });

  const {
    trustScore,
    events: proctorEvents,
    isProctoring,
    visionReady,
    audioReady,
    visionUnavailable,
    attachEditorDom,
    recordInternalCopy,
    ingestEvent,
    ingestSnapshot,
    ingestRemoteEvents,
  } = useProctor({
    enabled: profile.role === 'candidate' && Boolean(profile.proctorConsentAccepted),
    role: profile.role,
    send,
    videoRef: proctorVideoRef,
    audioStream: localStream,
    videoEnabled: cameraOn,
    code,
  });

  useEffect(() => {
    sendRef.current = send;
  }, [send]);

  useEffect(() => {
    ingestProctorEventRef.current = ingestEvent;
    ingestProctorEventsRef.current = ingestRemoteEvents;
    ingestProctorSnapshotRef.current = ingestSnapshot;
  }, [ingestEvent, ingestRemoteEvents, ingestSnapshot]);

  useEffect(() => {
    if (proctorVideoRef.current && localStream) {
      proctorVideoRef.current.srcObject = localStream;
    }
  }, [localStream]);

  const publishState = useCallback((nextState = {}) => {
    send('state_update', { ...roomState, ...nextState });
  }, [roomState, send]);

  useEffect(() => {
    if (profile.role !== 'interviewer') return;
    send('state_update', roomState);
  }, [profile.role, roomState, send]);

  useEffect(() => {
    const updateTheme = () => {
      setEditorTheme(document.documentElement.classList.contains('dark') ? 'vs-dark' : 'light');
    };
    const observer = new MutationObserver(updateTheme);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const updatedRoom = { ...readRoom(room.id), selectedQuestionId, aiEnabled };
    saveRoom(updatedRoom);
    onRoomChange(updatedRoom);
  }, [aiEnabled, onRoomChange, room.id, selectedQuestionId]);

  useEffect(() => {
    localStreamRef.current = localStream;
    if (!localStream) return;

    // Add any newly available tracks to existing peer connections
    // (handles the case where localStream became available AFTER connections were made)
    peerConnectionsRef.current.forEach((pc) => {
      const senderTrackIds = new Set(pc.getSenders().map((sender) => sender.track?.id).filter(Boolean));
      localStream.getTracks().forEach((track) => {
        if (!senderTrackIds.has(track.id)) {
          pc.addTrack(track, localStream);
        }
      });
    });
  }, [localStream]);

  useEffect(() => {
    setParticipantMap((current) => ({
      ...current,
      [profile.participantId]: {
        ...(current[profile.participantId] || {}),
        id: profile.participantId,
        name: profile.name,
        role: profile.role,
        micOn,
        cameraOn,
        status: mediaError ? 'Media permission needed' : (profile.role === 'interviewer' ? 'Leading the round' : 'Ready in room'),
        active: true,
      },
    }));
    send('participant_update', {
      participant_id: profile.participantId,
      name: profile.name,
      role: profile.role,
      micOn,
      cameraOn,
      status: mediaError ? 'Media permission needed' : (profile.role === 'interviewer' ? 'Leading the round' : 'Ready in room'),
      active: true,
    });
  }, [cameraOn, mediaError, micOn, profile.name, profile.participantId, profile.role, send]);

  const createPeerConnection = useCallback((peerId) => {
    if (!window.RTCPeerConnection) return null;
    const existing = peerConnectionsRef.current.get(peerId);
    if (existing) return existing;

    const pc = new RTCPeerConnection({
      iceServers: [
        { urls: 'stun:stun.l.google.com:19302' },
        { urls: 'stun:stun1.l.google.com:19302' },
        { urls: 'stun:stun2.l.google.com:19302' },
        { urls: 'stun:stun.cloudflare.com:3478' },
      ],
      iceCandidatePoolSize: 10,
    });

    localStreamRef.current?.getTracks().forEach((track) => {
      pc.addTrack(track, localStreamRef.current);
    });

    pc.onicecandidate = (event) => {
      if (!event.candidate) return;
      sendRef.current('rtc_signal', {
        to: peerId,
        from: profile.participantId,
        signal: { type: 'ice', candidate: event.candidate.toJSON() },
      });
    };

    pc.ontrack = (event) => {
      const [stream] = event.streams;
      if (stream) {
        setRemoteStreams((current) => ({ ...current, [peerId]: stream }));
      }
    };

    pc.onconnectionstatechange = () => {
      if (['failed', 'closed', 'disconnected'].includes(pc.connectionState)) {
        setRemoteStreams((current) => {
          const next = { ...current };
          delete next[peerId];
          return next;
        });
      }
    };

    peerConnectionsRef.current.set(peerId, pc);
    return pc;
  }, [profile.participantId]);

  const startOffer = useCallback(async (peerId) => {
    if (offeredPeersRef.current.has(peerId)) return;
    const pc = createPeerConnection(peerId);
    if (!pc || pc.signalingState !== 'stable') return;

    try {
      offeredPeersRef.current.add(peerId);
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      sendRef.current('rtc_signal', {
        to: peerId,
        from: profile.participantId,
        signal: pc.localDescription.toJSON(),
      });
    } catch {
      offeredPeersRef.current.delete(peerId);
    }
  }, [createPeerConnection, profile.participantId]);

  // Re-trigger offers when localStream becomes available after peer connections
  // were already established (stream arrives async via getUserMedia).
  useEffect(() => {
    if (!localStream) return;
    offeredPeersRef.current.forEach((peerId) => {
      const pc = peerConnectionsRef.current.get(peerId);
      if (!pc || pc.signalingState !== 'stable') return;
      const hasMediaSenders = pc.getSenders().some((s) => s.track);
      if (!hasMediaSenders) {
        offeredPeersRef.current.delete(peerId);
        startOffer(peerId);
      }
    });
  }, [localStream, startOffer]);

  const handleRtcSignal = useCallback(async (payload = {}) => {
    if (payload.to && payload.to !== profile.participantId) return;
    const peerId = payload.from;
    const signal = payload.signal;
    if (!peerId || !signal) return;

    const pc = createPeerConnection(peerId);
    if (!pc) return;

    try {
      if (signal.type === 'offer') {
        await pc.setRemoteDescription(new RTCSessionDescription(signal));
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        sendRef.current('rtc_signal', {
          to: peerId,
          from: profile.participantId,
          signal: pc.localDescription.toJSON(),
        });
      } else if (signal.type === 'answer') {
        if (pc.signalingState !== 'stable') {
          await pc.setRemoteDescription(new RTCSessionDescription(signal));
        }
      } else if (signal.type === 'ice' && signal.candidate) {
        await pc.addIceCandidate(new RTCIceCandidate(signal.candidate));
      }
    } catch {
      // WebRTC can fail on restrictive networks; chat/code/board still work.
    }
  }, [createPeerConnection, profile.participantId]);

  useEffect(() => {
    rtcSignalHandlerRef.current = handleRtcSignal;
  }, [handleRtcSignal]);

  useEffect(() => {
    participants
      .filter((participant) => !participant.isCurrent)
      .forEach((participant) => {
        if (profile.participantId > participant.id) {
          startOffer(participant.id);
        }
      });
  }, [participants, profile.participantId, startOffer]);

  useEffect(() => {
    return () => {
      sendRef.current('participant_left', { participant_id: profile.participantId });
      peerConnectionsRef.current.forEach((pc) => pc.close());
      peerConnectionsRef.current.clear();
    };
  }, [profile.participantId]);

  const handlePointerMove = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const nextPointer = {
      x: Math.max(3, Math.min(94, ((event.clientX - rect.left) / rect.width) * 100)),
      y: Math.max(5, Math.min(90, ((event.clientY - rect.top) / rect.height) * 100)),
    };
    setPointer(nextPointer);
    send('pointer_update', {
      participant_id: profile.participantId,
      name: profile.name,
      role: profile.role,
      pointer: nextPointer,
    });
  };

  const handleSend = () => {
    const body = draft.trim();
    if (!body) return;
    const message = {
      id: `m-${Date.now()}`,
      author: profile.name,
      body,
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    };
    setMessages((items) => [
      ...items,
      message,
    ]);
    send('chat_message', message);
    setDraft('');
  };

  const handleModeChange = (nextMode) => {
    setModeState(nextMode);
    publishState({ mode: nextMode });
  };

  const handleToggleEditorTheme = () => {
    setEditorTheme((currentTheme) => (currentTheme === 'light' ? 'vs-dark' : 'light'));
  };

  const handleCodeChange = (nextCode) => {
    setCode(nextCode);
    publishState({ code: nextCode });
  };

  const handleCustomInputChange = (nextInput) => {
    setCustomInput(nextInput);
    publishState({ customInput: nextInput });
  };

  const handleCodeSelectionChange = useCallback((selection) => {
    const participant = participants.find((item) => item.id === profile.participantId);
    const nextSelection = selection ? {
      ...selection,
      participantId: profile.participantId,
      name: profile.name,
      color: participant?.color || 'var(--lp-accent)',
      selectedAt: new Date().toISOString(),
    } : null;
    setCodeSelection(nextSelection);
    publishState({ codeSelection: nextSelection });
  }, [participants, profile.name, profile.participantId, publishState]);

  const handleLanguageChange = (nextLanguage) => {
    const nextCode = starterCode[nextLanguage] || starterCode.javascript;
    setLanguage(nextLanguage);
    setCode(nextCode);
    setRunResult(null);
    publishState({ language: nextLanguage, code: nextCode, runResult: null });
  };

  const handleQuestionSelect = (nextQuestionId) => {
    const nextQuestion = questionBank.find((question) => question.id === nextQuestionId);
    const nextLanguage = nextQuestion?.preferredLanguage || language;
    const nextCode = starterCode[nextLanguage] || starterCode.javascript;
    setSelectedQuestionId(nextQuestionId);
    setLanguage(nextLanguage);
    setCode(nextCode);
    setRunResult(null);
    publishState({ selectedQuestionId: nextQuestionId, language: nextLanguage, code: nextCode, runResult: null });
  };

  const handleWhiteboardSnapshot = useCallback((snapshot) => {
    setWhiteboardSnapshot(snapshot);
    publishState({ whiteboardSnapshot: snapshot });
  }, [publishState]);

  const runCodeWithInput = useCallback(async (input) => {
    const response = await api.post('/api/code/execute', {
      code,
      language,
      input,
    });
    return response.data;
  }, [code, language]);

  const handleRunCode = useCallback(async () => {
    if (isRunningCode || !code.trim()) return;
    setIsRunningCode(true);
    const startedBy = profile.name;
    try {
      if (isFrontendLanguage(language) || selectedQuestion?.type === 'frontend') {
        const nextResult = {
          mode: 'preview',
          status: 'Preview refreshed',
          startedBy,
          createdAt: new Date().toISOString(),
        };
        setRunResult(nextResult);
        publishState({ runResult: nextResult });
        return;
      }

      if (isSqlLanguage(language) || selectedQuestion?.type === 'sql') {
        const nextResult = {
          ...runSqlValidation(code, selectedQuestion),
          startedBy,
          createdAt: new Date().toISOString(),
        };
        setRunResult(nextResult);
        publishState({ runResult: nextResult });
        return;
      }

      const result = await runCodeWithInput(customInput);
      const nextResult = {
        mode: 'run',
        ...result,
        startedBy,
        createdAt: new Date().toISOString(),
      };
      setRunResult(nextResult);
      publishState({ runResult: nextResult });
    } catch (error) {
      const nextResult = {
        mode: 'run',
        status: 'Error',
        error: error.response?.data?.error || error.error || error.message || 'Execution failed.',
        startedBy,
        createdAt: new Date().toISOString(),
      };
      setRunResult(nextResult);
      publishState({ runResult: nextResult });
    } finally {
      setIsRunningCode(false);
    }
  }, [code, customInput, isRunningCode, language, profile.name, publishState, runCodeWithInput, selectedQuestion]);

  const handleRunTests = useCallback(async () => {
    if (isRunningCode || !selectedQuestion?.examples?.length || !code.trim()) return;
    setIsRunningCode(true);
    const startedBy = profile.name;
    try {
      if (isSqlLanguage(language) || selectedQuestion?.type === 'sql') {
        const nextResult = {
          ...runSqlValidation(code, selectedQuestion),
          startedBy,
          createdAt: new Date().toISOString(),
        };
        setRunResult(nextResult);
        publishState({ runResult: nextResult });
        return;
      }

      const tests = await Promise.all(selectedQuestion.examples.map(async (example) => {
        try {
          const result = await runCodeWithInput(example.input);
          const actual = (result.stdout || '').trim();
          const expected = (example.output || '').trim();
          return {
            input: example.input,
            expected: example.output,
            actual,
            stdout: result.stdout,
            stderr: result.stderr,
            error: result.error || result.compile_output,
            status: result.status,
            passed: actual === expected && !result.stderr && !result.error && !result.compile_output,
          };
        } catch (error) {
          return {
            input: example.input,
            expected: example.output,
            actual: '',
            error: error.response?.data?.error || error.error || error.message || 'Execution failed.',
            status: 'Error',
            passed: false,
          };
        }
      }));
      const passed = tests.filter((test) => test.passed).length;
      const nextResult = {
        mode: 'tests',
        tests,
        passed,
        total: tests.length,
        startedBy,
        createdAt: new Date().toISOString(),
      };
      setRunResult(nextResult);
      publishState({ runResult: nextResult });
    } finally {
      setIsRunningCode(false);
    }
  }, [code, isRunningCode, language, profile.name, publishState, runCodeWithInput, selectedQuestion]);

  const publishAiMessages = useCallback((nextMessages) => {
    setAiMessages(nextMessages);
    publishState({ aiMessages: nextMessages });
  }, [publishState]);

  const handleAiSend = useCallback(async () => {
    const body = aiDraft.trim();
    if (!body || isAiThinking || !aiEnabled || profile.role === 'interviewer') return;

    const userMessage = {
      id: `ai-user-${Date.now()}`,
      role: 'user',
      author: profile.name,
      text: body,
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    };
    const nextMessages = [...aiMessages, userMessage];
    publishAiMessages(nextMessages);
    setAiDraft('');
    setIsAiThinking(true);

    const context = [
      `Room: ${room.title}`,
      `Role: ${room.role}`,
      `Team: ${room.team}`,
      selectedQuestion ? `Selected question: ${selectedQuestion.title}\n${selectedQuestion.prompt}` : 'Selected question: none',
      `Language: ${language}`,
      `Current code:\n${code.slice(0, 5000)}`,
      codeSelection?.text ? `Currently selected code by ${codeSelection.name}:\n${codeSelection.text}` : '',
    ].filter(Boolean).join('\n\n');

    try {
      const response = await api.post('/api/coach/chat', {
        message: `${body}\n\nUse this live coding interview context. Help the candidate reason through the problem without hiding that this AI help is visible to the interviewer.\n\n${context}`,
        article_title: 'Live coding interview',
        article_content: context,
      });
      const assistantMessage = {
        id: `ai-assistant-${Date.now()}`,
        role: 'assistant',
        author: 'AI assistant',
        text: response.data?.message || 'I can help you reason through the next step.',
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      };
      publishAiMessages([...nextMessages, assistantMessage]);
    } catch (error) {
      const assistantMessage = {
        id: `ai-assistant-${Date.now()}`,
        role: 'assistant',
        author: 'AI assistant',
        text: error.response?.status === 401
          ? 'AI help is enabled for this room, but you need to be signed in to use it.'
          : 'AI help is unavailable right now. Please try again in a moment.',
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      };
      publishAiMessages([...nextMessages, assistantMessage]);
    } finally {
      setIsAiThinking(false);
    }
  }, [
    aiDraft,
    aiEnabled,
    aiMessages,
    code,
    codeSelection,
    isAiThinking,
    language,
    profile.name,
    profile.role,
    publishAiMessages,
    room.role,
    room.team,
    room.title,
    selectedQuestion,
  ]);

  const getParticipantStream = (participant) => (
    participant.isCurrent ? localStream : remoteStreams[participant.id]
  );

  const handleLeave = () => {
    send('participant_left', { participant_id: profile.participantId });
    peerConnectionsRef.current.forEach((pc) => pc.close());
    peerConnectionsRef.current.clear();
    onLeave();
  };

  const closeSidePanels = () => {
    setParticipantsOpen(false);
    setAiOpen(false);
    setChatOpen(false);
    setProctorOpen(false);
  };

  const activeSidePanel = proctorOpen && profile.role === 'interviewer'
    ? (
      <ProctorPanel
        events={proctorEvents}
        trustScore={trustScore}
        visionReady={visionReady}
        audioReady={audioReady}
        visionUnavailable={visionUnavailable}
        isProctoring={isProctoring}
        onClose={() => setProctorOpen(false)}
      />
    )
    : participantsOpen
      ? <ParticipantsPanel participants={participants} onClose={() => setParticipantsOpen(false)} />
      : aiOpen
        ? (
          <AIAssistantPanel
            messages={aiMessages}
            draft={aiDraft}
            setDraft={setAiDraft}
            onSend={handleAiSend}
            onClose={() => setAiOpen(false)}
            isThinking={isAiThinking}
            canSend={profile.role !== 'interviewer'}
          />
        )
      : chatOpen
        ? <ChatPanel messages={messages} draft={draft} setDraft={setDraft} onSend={handleSend} onClose={() => setChatOpen(false)} />
        : null;

  return (
    <div className="landing-page business-interview-room flex h-screen min-h-[720px] flex-col bg-white text-[var(--lp-ink)]">
      {profile.role === 'candidate' && (
        <video
          ref={proctorVideoRef}
          autoPlay
          playsInline
          muted
          className="pointer-events-none fixed h-px w-px opacity-0"
        />
      )}
      <main className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
        <div className="flex h-16 shrink-0 items-center justify-between border-b border-[var(--lp-line)] bg-white px-4 lg:px-6">
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              onClick={handleLeave}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-[var(--lp-ink-soft)] transition hover:bg-[var(--lp-surface)]"
              title="Leave room"
            >
              <Icon name="ChevronLeft" size={20} />
            </button>
            <span className="truncate text-base font-semibold text-[var(--lp-ink)] max-w-[220px] sm:max-w-none">
              {room.title}
            </span>
            <span className="hidden whitespace-nowrap rounded-full border border-[var(--lp-line)] px-3 py-1 text-xs font-medium text-[var(--lp-ink-soft)] sm:inline-block">
              {room.role} • {room.team}
            </span>
            {selectedQuestion && (
              <span className="hidden whitespace-nowrap rounded-full border border-[var(--lp-accent)]/15 bg-[var(--lp-accent)]/10 px-3 py-1 text-xs font-medium text-[var(--lp-accent)] md:inline-block">
                {selectedQuestion.title}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <span className="hidden rounded-full border border-[var(--lp-accent)]/15 bg-[var(--lp-accent)]/10 px-3 py-1 text-xs font-semibold text-[var(--lp-accent)] sm:inline-flex">
              {syncStatus === 'realtime' ? 'Realtime' : syncStatus === 'local' ? 'Local' : 'Connecting'}
            </span>
            <span className="rounded-full border border-[var(--lp-line)] px-3 py-1 text-xs font-medium text-[var(--lp-ink-soft)]">
              {participants.length} {participants.length === 1 ? 'participant' : 'participants'}
            </span>
            {mediaError && (
              <span className="hidden rounded-full border border-amber-300 bg-amber-50 px-3 py-1 text-xs text-amber-700 sm:inline-flex">
                Media blocked
              </span>
            )}
          </div>
        </div>

        <div className="relative flex min-h-0 flex-1 overflow-hidden">
          <section className={cn(
            'flex min-w-0 flex-1 flex-col overflow-y-auto lg:overflow-hidden',
            mode === 'meet' ? 'p-3 pb-24 lg:p-4 lg:pb-24' : 'p-0'
          )}>
            {mode === 'meet' && (
              participants.length === 1 ? (
                <div className="flex min-h-0 flex-1">
                  <VideoTile
                    participant={participants[0]}
                    stream={getParticipantStream(participants[0])}
                    large
                    full
                  />
                </div>
              ) : (
                <div className="grid min-h-0 flex-1 gap-3 md:grid-cols-2">
                  {participants.map((participant, index) => (
                    <VideoTile key={participant.id} participant={participant} stream={getParticipantStream(participant)} large={index === 0} />
                  ))}
                </div>
              )
            )}

            {mode === 'ide' && (
              <div className="relative min-h-0 flex-1">
                <IdeWorkspace
                  code={code}
                  language={language}
                  participants={participants}
                  pointer={pointer}
                  selectedQuestion={selectedQuestion}
                  selectedQuestionId={selectedQuestionId}
                  canSelect={canSelectQuestion}
                  editorTheme={editorTheme}
                  onCodeChange={handleCodeChange}
                  onLanguageChange={handleLanguageChange}
                  onPointerMove={handlePointerMove}
                  onQuestionSelect={handleQuestionSelect}
                  currentParticipantId={profile.participantId}
                  codeSelection={codeSelection}
                  onCodeSelectionChange={handleCodeSelectionChange}
                  customInput={customInput}
                  onCustomInputChange={handleCustomInputChange}
                  runResult={runResult}
                  isRunningCode={isRunningCode}
                  onRunCode={handleRunCode}
                  onRunTests={handleRunTests}
                  onToggleEditorTheme={handleToggleEditorTheme}
                  onEditorDomReady={profile.role === 'candidate' ? attachEditorDom : undefined}
                  onInternalCopy={profile.role === 'candidate' ? recordInternalCopy : undefined}
                />
                <FloatingVideoStrip participants={participants} getParticipantStream={getParticipantStream} />
              </div>
            )}

            {mode === 'whiteboard' && (
              <div className="relative min-h-0 flex-1">
                <WhiteboardWorkspace
                  roomId={room.id}
                  participants={participants}
                  pointer={pointer}
                  snapshot={whiteboardSnapshot}
                  onPointerMove={handlePointerMove}
                  onSnapshotChange={handleWhiteboardSnapshot}
                />
                <FloatingVideoStrip participants={participants} getParticipantStream={getParticipantStream} />
              </div>
            )}
          </section>

          {activeSidePanel && (
            <div className="absolute inset-3 z-30 flex min-h-0 lg:static lg:inset-auto lg:z-auto">
              {activeSidePanel}
            </div>
          )}
        </div>

        <div className="business-room-controls">
          <div className="pointer-events-auto flex max-w-full flex-wrap items-center justify-center gap-2 rounded-full border border-white/10 bg-[#1f2124]/95 p-2 shadow-2xl shadow-black/25 backdrop-blur-md">
            {/* <IconControl icon={micOn ? 'Mic' : 'MicOff'} active={micOn} label={micOn ? 'Mute' : 'Unmute'} onClick={() => setMicOn((value) => !value)} /> */}
            <IconControl icon={cameraOn ? 'Video' : 'VideoOff'} active={cameraOn} label={cameraOn ? 'Camera off' : 'Camera on'} onClick={() => setCameraOn((value) => !value)} />
            <IconControl icon="MessageSquare" active={chatOpen} label="Chat" onClick={() => { const next = !chatOpen; closeSidePanels(); setChatOpen(next); }} />
            {aiEnabled && (
              <IconControl icon="Sparkles" active={aiOpen} label="AI help" onClick={() => { const next = !aiOpen; closeSidePanels(); setAiOpen(next); }} />
            )}
            <IconControl icon="Users" active={participantsOpen} label="Participants" onClick={() => { const next = !participantsOpen; closeSidePanels(); setParticipantsOpen(next); }} />
            {profile.role === 'interviewer' && (
              <IconControl
                icon="Shield"
                active={proctorOpen}
                label="Proctor logs"
                onClick={() => {
                  const next = !proctorOpen;
                  closeSidePanels();
                  setProctorOpen(next);
                }}
              />
            )}
            <span className="mx-1 hidden h-8 w-px bg-white/10 sm:block" />
            <IconControl icon="Monitor" active={mode === 'meet'} label="Meet mode" onClick={() => handleModeChange('meet')} />
            <IconControl icon="Code2" active={mode === 'ide'} label="Open IDE" onClick={() => handleModeChange('ide')} />
            <IconControl icon="PenTool" active={mode === 'whiteboard'} label="Open whiteboard" onClick={() => handleModeChange('whiteboard')} />
            <span className="mx-1 hidden h-8 w-px bg-white/10 sm:block" />
            <IconControl icon="PhoneOff" danger label="Leave room" onClick={handleLeave} />
          </div>
        </div>
      </main>
    </div>
  );
};

const BusinessInterviewsPage = () => {
  const { roomId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [room, setRoom] = useState(() => readRoom(roomId));
  const [profile, setProfile] = useState(null);
  const isFeedbackPage = location.pathname.endsWith('/feedback');

  useEffect(() => {
    if (!roomId) return;
    const currentRoom = readRoom(roomId);
    saveRoom(currentRoom);
    setRoom(currentRoom);
  }, [roomId]);

  if (!roomId) return <CreateInterviewScreen />;
  if (!room) return null;
  if (isFeedbackPage) return <BusinessInterviewFeedbackScreen room={room} />;

  if (!profile) {
    return <JoinInterviewScreen room={room} onJoin={setProfile} />;
  }

  return (
    <InterviewRoom
      room={room}
      profile={profile}
      onRoomChange={setRoom}
      onLeave={() => {
        setProfile(null);
        navigate(`/for-businesses/interviews/${room.id}/feedback`);
      }}
    />
  );
};

export default BusinessInterviewsPage;
